(() => {
    'use strict';
    // Semitonos PRO — v2.13
    // v2.9: motor circular estable por celda. Elimina interpolación de cobertura dentro de cada celda,
    // evita transiciones punto/hueco parciales que producían líneas, X e I, y reduce el piso mínimo
    // que anulaba visualmente el control de Tamaño Máximo. Punto y hueco permanecen circulares.
    // v2.7: la transición de tramado de puntos a tramado de perforaciones
    // generaba formas aleatorias. Causa: al pasar el umbral de contacto
    // (coverage = π/4 ≈ 78.5%), el agujero de la fase "perforación" se
    // centraba en el MISMO punto que el centro del círculo de tinta. Eso
    // producía una inversión brusca: el centro de la celda (siempre tinta en
    // fase punto) se volvía agujero de golpe, y las 4 esquinas (siempre fondo
    // en fase punto) se volvían tinta de golpe — un salto de forma real,
    // aunque el área total coincidiera. Se nota más donde el tono cambia
    // rápido entre celdas vecinas. Corrección: en fase perforación, el
    // agujero ahora se centra en la ESQUINA más cercana de la celda (donde de
    // verdad queda el último hueco sin cubrir mientras el punto crece y se
    // funde con sus vecinos), no en el centro. Verificado numéricamente: el
    // área sigue coincidiendo con la cobertura pedida en todo el rango, y el
    // centro de la celda ya no se invierte al cruzar el umbral.
    // Semitonos PRO — v2.6
    // v2.6: al mover el umbral de protección de oscuros/luces aparecían formas
    // raras (rombos, octágonos, cruces) en vez de círculos limpios. Causa: las
    // 9 sub-muestras de antialiasing de cada pixel calculaban SU PROPIA celda
    // más cercana con Math.round(sampleGX/Y), en vez de usar la misma celda
    // (cellX, cellY) ya usada para calcular la cobertura/radio de ese pixel.
    // Cerca de un borde de celda, una sub-muestra podía "brincar" a la celda
    // vecina y probarse contra el radio de un punto que no era el suyo,
    // mezclando dos círculos distintos dentro de un mismo pixel. Al mover el
    // umbral la cobertura entre celdas vecinas cambia más bruscamente, así que
    // el error se volvía mucho más visible. Ahora las 9 sub-muestras miden
    // distancia siempre contra la misma (cellX, cellY) fija del pixel.
    // v2.5: los puntos ya se veían redondeados (antialiasing de v2.4), pero
    // seguían "pegándose" en líneas rectas. La causa era el ÁNGULO de la trama,
    // no la forma del punto: a 0°/90° las filas de puntos quedan alineadas con
    // la cuadrícula de pixeles y, al crecer, se unen en líneas horizontales/
    // verticales muy visibles. Se fijó 45° como ángulo de partida (el mismo que
    // usa Semitonos Fáciles y el estándar de cualquier trama de una tinta),
    // dejando el control deslizante de ángulo libre para que el usuario lo
    // mueva si quiere. Ver el bloque junto a "Inicializar UI" al final del
    // archivo.
    // v2.4: el piso mínimo de separación (v2.3) no alcanzaba: con separaciones
    // chicas, el campo senoidal se seguía muestreando en un único punto por
    // pixel, así que el borde de cada punto Y de cada perforación quedaba en
    // escalones de 1 pixel que a simple vista parecen rectángulos, sobre todo
    // en los huecos de las zonas más oscuras. createContinuousRoundWaveMask
    // ahora evalúa 3×3 sub-posiciones por pixel (antialiasing real) y guarda
    // un alfa gradual 0–255 en vez de una decisión binaria. Así los puntos y
    // las perforaciones quedan redondeados de verdad, igual que Semitonos
    // Fáciles pero perfeccionado. Se retiró la llamada a
    // removeSinglePixelMaskSpecks() en esta ruta porque el antialiasing ya
    // impide el ruido de un solo pixel que esa función recortaba.
    // v2.3: corregido el tramado circular, que generaba una cuadrícula/ruido en
    // vez de puntos redondos. Causa: `separacion` se tomaba tal cual del slider,
    // sin el piso mínimo de 4 px que sí aplica el motor de Semitonos Fáciles. Con
    // separaciones menores a ese piso, el campo senoidal que dibuja los puntos ya
    // no tiene pixeles suficientes por periodo para resolver un círculo real y el
    // resultado se ve como una trama cuadrada. Se restauró ese piso mínimo (ver
    // más abajo, junto a `const separacion =`), igual que en Semitonos Fáciles.
    // v2.2: se eliminó por completo la opción "Tramado profesional sin perder
    // color" (checkbox, slider, render dedicado) a pedido del cliente. El
    // semitono normal (máscara de puntos sobre la foto real) es ahora el único
    // modo, tal como funcionaba antes de introducir esa opción.

    const appRoot = document.getElementById('semitonos-app');
    if (!appRoot || appRoot.dataset.semitonosInitialized === 'true') return;
    appRoot.dataset.semitonosInitialized = 'true';

    // ═══════════════════════════════════════════════════════════════════
            // ELEMENTOS DEL DOM
            // ═══════════════════════════════════════════════════════════════════

            const originalCanvas = document.getElementById('originalCanvas');
            const outputCanvas = document.getElementById('outputCanvas');
            const originalCtx = originalCanvas.getContext('2d', { willReadFrequently: true });
            const outputCtx = outputCanvas.getContext('2d');
            const renderCanvas = document.createElement('canvas');
            const renderCtx = renderCanvas.getContext('2d', { willReadFrequently: true });
            const canvasWrapper = document.getElementById('canvasWrapper');
            const canvasContainer = document.getElementById('canvasContainer');

            // Controles
            const imageUpload = document.getElementById('imageUpload');
            const uploadZone = document.getElementById('uploadZone');
            const globalDropOverlay = document.getElementById('globalDropOverlay');
            const zoomInput = document.getElementById('zoom');
            const resetViewBtn = document.getElementById('resetViewBtn');
            const bgRemovalInput = document.getElementById('bgRemoval');
            const thresholdInput = document.getElementById('threshold');
            const separationInput = document.getElementById('separation');
            const maxSizeInput = document.getElementById('maxSize');
            const screenAngleInput = document.getElementById('screenAngle');
            const gammaInput = document.getElementById('gamma');
            const dotShapeSelect = document.getElementById('dotShape');
            const hardEdgesCheckbox = document.getElementById('hardEdges');
            // Se instala aquí para conservar el HTML y CSS existentes.
            const fullScreenGroup = document.createElement('div');
            fullScreenGroup.className = 'control-group';
            fullScreenGroup.innerHTML = `
                <label class="checkbox-wrapper" for="fullHalftone">
                    <input type="checkbox" id="fullHalftone" aria-describedby="fullHalftoneHelp">
                    <span>Tramado completo</span>
                </label>
                <p class="help-text" id="fullHalftoneHelp">Reduce la plasta también en blancos y colores claros, después de eliminar el color principal. Respeta los colores protegidos y el pincel restaurador.</p>
            `;
            const fullScreenAnchor = document.getElementById('protectColorsEnabled').closest('.checkbox-wrapper') || document.getElementById('protectColorsEnabled').parentElement;
            fullScreenAnchor.insertAdjacentElement('beforebegin', fullScreenGroup);
            const fullHalftoneCheckbox = fullScreenGroup.querySelector('#fullHalftone');
            fullHalftoneCheckbox.addEventListener('change', () => processImage(0));

            const protectColorsEnabled = document.getElementById('protectColorsEnabled');
            const protectedColorsGrid = document.getElementById('protectedColorsGrid');
            const addProtectedColorBtn = document.getElementById('addProtectedColorBtn');
            const protectedColorLimit = document.getElementById('protectedColorLimit');
            const protectedColorToleranceInput = document.getElementById('protectedColorTolerance');
            const protectedColorToleranceValue = document.getElementById('protectedColorToleranceValue');
            const restoreBrushBtn = document.getElementById('restoreBrushBtn');
            const restoreBrushSizeInput = document.getElementById('restoreBrushSize');
            const restoreBrushSizeValue = document.getElementById('restoreBrushSizeValue');
            const clearBrushBtn = document.getElementById('clearBrushBtn');
            const brushCursor = document.getElementById('brushCursor');
            const downloadBtn = document.getElementById('downloadBtn');
            const processingMessage = document.getElementById('processingMessage');
            const exportWidthInput = document.getElementById('exportWidth');
            const exportHeightInput = document.getElementById('exportHeight');
            const exportUnitSelect = document.getElementById('exportUnit');
            const exportDpiInput = document.getElementById('exportDpi');
            const lockExportRatioCheckbox = document.getElementById('lockExportRatio');
            const exportSizeHelp = document.getElementById('exportSizeHelp');
            const garmentModeSelect = document.getElementById('garmentMode');

            // Referencias del Mejorador Unificado (NUEVO)
            const enhancerControlsGroup = document.getElementById('enhancerControlsGroup');
            const improveImageBtn = document.getElementById('improveImageBtn');

            // Labels dinámicos
            const bgRemovalLabel = document.getElementById('bgRemovalLabel');
            const bgRemovalHelp = document.getElementById('bgRemovalHelp');
            const thresholdLabel = document.getElementById('thresholdLabel');
            const thresholdHelp = document.getElementById('thresholdHelp');
            const garmentModeHelp = document.getElementById('garmentModeHelp');
            // Referencias para el modo de prenda personalizado (NUEVO)
            const customGarmentColorInput = document.getElementById('customGarmentColor');
            const customGarmentColorGroup = document.getElementById('customGarmentColorGroup');
            const garmentColorBgBtn = document.getElementById('garmentColorBgBtn');

            // Value displays
            const zoomValue = document.getElementById('zoomValue');
            const bgRemovalValue = document.getElementById('bgRemovalValue');
            const thresholdValue = document.getElementById('thresholdValue');
            const separationValue = document.getElementById('separationValue');
            const maxSizeValue = document.getElementById('maxSizeValue');
            const screenAngleValue = document.getElementById('screenAngleValue');
            const gammaValue = document.getElementById('gammaValue');

            let currentImage = null;
            let isPanning = false;
            let lastX = 0;
            let lastY = 0;
            let originalFileName = 'imagen';

            // Estado de protección de color y pincel restaurador
            let activeColorPickerIndex = -1;
            let isRestoreBrushActive = false;
            let isBrushDrawing = false;
            let lastBrushPoint = null;
            let processTimer = null;
            let processFrame = null;
            let processRequestId = 0;

            const MAX_PERSISTENT_CACHE_PIXELS = 12000000;
            const preparedSourceCache = {
                image: null,
                width: 0,
                height: 0,
                originalImageData: null,
                backgroundKey: '',
                connectedBackgroundMask: null
            };

            const baseProcessedCanvas = document.createElement('canvas');
            const baseProcessedCtx = baseProcessedCanvas.getContext('2d', { willReadFrequently: true });
            const manualRestoreMaskCanvas = document.createElement('canvas');
            const manualRestoreMaskCtx = manualRestoreMaskCanvas.getContext('2d');
            const restoreCompositeCanvas = document.createElement('canvas');
            const restoreCompositeCtx = restoreCompositeCanvas.getContext('2d');
            const protectedRestoreCanvas = document.createElement('canvas');
            const protectedRestoreCtx = protectedRestoreCanvas.getContext('2d');
            const inkMaskCanvas = document.createElement('canvas');
            const inkMaskCtx = inkMaskCanvas.getContext('2d', { willReadFrequently: true });

            // ── ESTADO DEL MEJORADOR (NUEVO) ──
            const ENHANCER_CONFIG = {
                WORKER_URL: 'https://novage-enhancer.irvingkumul.workers.dev',
                MAX_FILE_SIZE_MB: 10,
                COOLDOWN_SECONDS: 15
            };

            let enhancerState = {
                currentFile: null, // Archivo original cargado
                turnstileToken: null,
                isProcessingEnhancement: false,
                cooldownRemaining: 0,
                pendingTurnstileResolve: null,
                pendingTurnstileReject: null,
                turnstileTimeout: null
            };

            let loadingOverlay = null;
            let loadingOverlayLabel = null;

            function ensureLoadingOverlay() {
                if (loadingOverlay) return;
                loadingOverlay = document.createElement('div');
                loadingOverlay.className = 'preview-loader';
                loadingOverlay.setAttribute('aria-hidden', 'true');
                loadingOverlay.innerHTML = `
                    <div class="preview-loader__box">
                        <div class="preview-loader__spinner"></div>
                        <div class="preview-loader__label">Procesando…</div>
                        <div class="preview-loader__bar"><span></span></div>
                    </div>
                `;
                canvasContainer.appendChild(loadingOverlay);
                loadingOverlayLabel = loadingOverlay.querySelector('.preview-loader__label');
            }

            function setProcessingOverlay(active, label = 'Procesando…') {
                ensureLoadingOverlay();
                if (loadingOverlayLabel) loadingOverlayLabel.textContent = label;
                loadingOverlay.classList.toggle('visible', !!active);
                loadingOverlay.setAttribute('aria-hidden', active ? 'false' : 'true');
            }

            // Estado de la vista (panning libre sin límites de scroll)
            let panX = 0;
            let panY = 0;
            let currentZoom = 100; // en porcentaje
            let exportAspectRatio = 1;
            let isSyncingExportSize = false;

            function applyTransform() {
                canvasWrapper.style.transform =
                    `translate(${panX}px, ${panY}px) scale(${currentZoom / 100})`;
            }

            ensureLoadingOverlay();

            function resizeAuxiliaryCanvases(width, height, clearManualMask = true) {
                [baseProcessedCanvas, restoreCompositeCanvas, protectedRestoreCanvas].forEach((canvas) => {
                    canvas.width = width;
                    canvas.height = height;
                });

                if (manualRestoreMaskCanvas.width !== width || manualRestoreMaskCanvas.height !== height) {
                    manualRestoreMaskCanvas.width = width;
                    manualRestoreMaskCanvas.height = height;
                    clearManualMask = true;
                }

                if (clearManualMask) {
                    manualRestoreMaskCtx.clearRect(0, 0, width, height);
                }
            }

            function invalidatePreparedSourceCache() {
                preparedSourceCache.image = null;
                preparedSourceCache.width = 0;
                preparedSourceCache.height = 0;
                preparedSourceCache.originalImageData = null;
                preparedSourceCache.backgroundKey = '';
                preparedSourceCache.connectedBackgroundMask = null;
            }

            function isProcessingRequestStale(requestId) {
                return requestId !== processRequestId;
            }

            function prepareSourceForProcessing(
                width,
                height,
                garmentMode,
                bgTolerance,
                tR,
                tG,
                tB
            ) {
                const sourceIdentityChanged = preparedSourceCache.image !== currentImage ||
                    preparedSourceCache.width !== width ||
                    preparedSourceCache.height !== height;

                if (sourceIdentityChanged) {
                    originalCanvas.width = width;
                    originalCanvas.height = height;
                    resizeAuxiliaryCanvases(width, height, true);

                    originalCtx.clearRect(0, 0, width, height);
                    originalCtx.imageSmoothingEnabled = true;
                    originalCtx.imageSmoothingQuality = 'high';
                    originalCtx.drawImage(currentImage, 0, 0, width, height);

                    preparedSourceCache.image = currentImage;
                    preparedSourceCache.width = width;
                    preparedSourceCache.height = height;
                    preparedSourceCache.backgroundKey = '';
                    preparedSourceCache.connectedBackgroundMask = null;
                    preparedSourceCache.originalImageData = null;
                }

                if (!preparedSourceCache.originalImageData) {
                    preparedSourceCache.originalImageData = originalCtx.getImageData(0, 0, width, height);
                }

                const originalData = preparedSourceCache.originalImageData.data;
                const backgroundKey = [
                    garmentMode,
                    bgTolerance,
                    tR,
                    tG,
                    tB
                ].join('|');

                if (preparedSourceCache.backgroundKey !== backgroundKey ||
                    !preparedSourceCache.connectedBackgroundMask) {
                    const connectedBackgroundMask = buildConnectedBackgroundMask(
                        originalData,
                        width,
                        height,
                        garmentMode,
                        bgTolerance,
                        tR,
                        tG,
                        tB
                    );
                    const baseImageData = originalCtx.createImageData(width, height);
                    const baseData = baseImageData.data;
                    baseData.set(originalData);

                    for (let pixelIndex = 0; pixelIndex < connectedBackgroundMask.length; pixelIndex++) {
                        if (connectedBackgroundMask[pixelIndex]) {
                            baseData[pixelIndex * 4 + 3] = 0;
                        }
                    }

                    baseProcessedCtx.clearRect(0, 0, width, height);
                    baseProcessedCtx.putImageData(baseImageData, 0, 0);

                    preparedSourceCache.backgroundKey = backgroundKey;
                    preparedSourceCache.connectedBackgroundMask = connectedBackgroundMask;
                }

                return {
                    originalData,
                    connectedBackgroundMask: preparedSourceCache.connectedBackgroundMask,
                    outputData: originalData
                };
            }

            function hexToRgb(hex) {
                return {
                    r: parseInt(hex.slice(1, 3), 16),
                    g: parseInt(hex.slice(3, 5), 16),
                    b: parseInt(hex.slice(5, 7), 16)
                };
            }

            function rgbToHex(r, g, b) {
                return '#' + [r, g, b]
                    .map((value) => Math.max(0, Math.min(255, value)).toString(16).padStart(2, '0'))
                    .join('');
            }

            function getProtectedColorSlots() {
                return Array.from(protectedColorsGrid.querySelectorAll('.protected-color-slot'));
            }

            function getProtectedColorInputs() {
                return Array.from(protectedColorsGrid.querySelectorAll('.protected-color-input'));
            }

            function getProtectedColorEnabledInputs() {
                return Array.from(protectedColorsGrid.querySelectorAll('.protected-color-enabled'));
            }

            function getProtectedColorPickerButtons() {
                return Array.from(protectedColorsGrid.querySelectorAll('.color-picker-btn'));
            }

            function updateProtectedColorUI() {
                const slots = getProtectedColorSlots();
                slots.forEach((slot, index) => {
                    const label = slot.querySelector('.protected-color-label');
                    const input = slot.querySelector('.protected-color-input');
                    const picker = slot.querySelector('.color-picker-btn');
                    const remove = slot.querySelector('.remove-protected-color-btn');
                    const number = index + 1;
                    label.textContent = `Color ${number}`;
                    input.setAttribute('aria-label', `Color protegido ${number}`);
                    picker.setAttribute('aria-label', `Tomar color ${number} de la imagen`);
                    remove.style.visibility = slots.length === 1 ? 'hidden' : 'visible';
                });
                protectedColorLimit.textContent = `${slots.length} de 5 colores`;
                addProtectedColorBtn.disabled = slots.length >= 5;
                addProtectedColorBtn.textContent = slots.length >= 5 ? 'Máximo de 5 colores' : '+ Agregar color';
            }

            function createProtectedColorSlot(value = '#ffffff') {
                const slot = document.createElement('div');
                slot.className = 'protected-color-slot';
                slot.innerHTML = `
                    <input type="checkbox" class="protected-color-enabled" checked aria-label="Activar color">
                    <div class="protected-color-input-wrap">
                        <input type="color" class="protected-color-input" value="${value}" aria-label="Color protegido">
                    </div>
                    <span class="protected-color-label">Color</span>
                    <button type="button" class="color-picker-btn" title="Tomar color de la imagen" aria-label="Tomar color de la imagen">
                        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M19 11a1 1 0 0 0-1 1v1.17l-2.59-2.58a3.5 3.5 0 0 0-.82-4.59l-1.58-1.2a2 2 0 0 0-2.8.39L6.4 10.31a2 2 0 0 0 .39 2.8l1.58 1.2a3.5 3.5 0 0 0 4.59-.82L15.83 16H14a1 1 0 1 0 0 2h5a1 1 0 0 0 1-1v-5a1 1 0 0 0-1-1ZM9.58 12.72l-1.58-1.2 3.8-5 1.58 1.2ZM5 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z"/></svg>
                    </button>
                    <button type="button" class="remove-protected-color-btn" title="Eliminar color" aria-label="Eliminar color">×</button>`;
                return slot;
            }

            function getActiveProtectedColors() {
                if (!protectColorsEnabled.checked) return [];
                return getProtectedColorSlots()
                    .map((slot) => {
                        const enabled = slot.querySelector('.protected-color-enabled');
                        const input = slot.querySelector('.protected-color-input');
                        return enabled.checked ? hexToRgb(input.value) : null;
                    })
                    .filter(Boolean);
            }

            function getCanvasPointFromClient(clientX, clientY) {
                const rect = canvasContainer.getBoundingClientRect();
                const scale = currentZoom / 100;
                return {
                    x: (clientX - rect.left - panX) / scale,
                    y: (clientY - rect.top - panY) / scale
                };
            }

            function isPointInsideCanvas(point) {
                return point.x >= 0 && point.y >= 0 &&
                       point.x < outputCanvas.width && point.y < outputCanvas.height;
            }

            function updateBrushCursor(clientX, clientY) {
                if (!isRestoreBrushActive) {
                    brushCursor.style.display = 'none';
                    return;
                }
                const visualSize = parseFloat(restoreBrushSizeInput.value) * (currentZoom / 100);
                const rect = canvasContainer.getBoundingClientRect();
                brushCursor.style.width = `${visualSize}px`;
                brushCursor.style.height = `${visualSize}px`;
                brushCursor.style.left = `${clientX - rect.left - visualSize / 2}px`;
                brushCursor.style.top = `${clientY - rect.top - visualSize / 2}px`;
                brushCursor.style.display = 'block';
            }

            function restoreThroughManualMask(targetCtx = outputCtx) {
                if (!baseProcessedCanvas.width || !baseProcessedCanvas.height) return;
                restoreCompositeCtx.clearRect(0, 0, restoreCompositeCanvas.width, restoreCompositeCanvas.height);
                restoreCompositeCtx.globalCompositeOperation = 'source-over';
                restoreCompositeCtx.drawImage(baseProcessedCanvas, 0, 0);
                restoreCompositeCtx.globalCompositeOperation = 'destination-in';
                restoreCompositeCtx.drawImage(manualRestoreMaskCanvas, 0, 0);
                restoreCompositeCtx.globalCompositeOperation = 'source-over';
                targetCtx.drawImage(restoreCompositeCanvas, 0, 0);
            }

            function drawRestoreStroke(fromPoint, toPoint) {
                const brushSize = parseFloat(restoreBrushSizeInput.value);
                manualRestoreMaskCtx.save();
                manualRestoreMaskCtx.strokeStyle = '#ffffff';
                manualRestoreMaskCtx.fillStyle = '#ffffff';
                manualRestoreMaskCtx.lineWidth = brushSize;
                manualRestoreMaskCtx.lineCap = 'round';
                manualRestoreMaskCtx.lineJoin = 'round';
                manualRestoreMaskCtx.beginPath();
                manualRestoreMaskCtx.moveTo(fromPoint.x, fromPoint.y);
                manualRestoreMaskCtx.lineTo(toPoint.x, toPoint.y);
                manualRestoreMaskCtx.stroke();
                manualRestoreMaskCtx.beginPath();
                manualRestoreMaskCtx.arc(toPoint.x, toPoint.y, brushSize / 2, 0, Math.PI * 2);
                manualRestoreMaskCtx.fill();
                manualRestoreMaskCtx.restore();

                outputCtx.save();
                outputCtx.beginPath();
                outputCtx.moveTo(fromPoint.x, fromPoint.y);
                outputCtx.lineTo(toPoint.x, toPoint.y);
                outputCtx.lineWidth = brushSize;
                outputCtx.lineCap = 'round';
                outputCtx.lineJoin = 'round';
                outputCtx.clip();
                outputCtx.drawImage(baseProcessedCanvas, 0, 0);
                outputCtx.restore();

                outputCtx.save();
                outputCtx.beginPath();
                outputCtx.arc(toPoint.x, toPoint.y, brushSize / 2, 0, Math.PI * 2);
                outputCtx.clip();
                outputCtx.drawImage(baseProcessedCanvas, 0, 0);
                outputCtx.restore();
            }

            // ═══════════════════════════════════════════════════════════════════
            // ACTUALIZACIÓN DINÁMICA DE UI SEGÚN MODO
            // ═══════════════════════════════════════════════════════════════════

            function updateUIForMode() {
                const mode = garmentModeSelect.value;
                // Tomar siempre los valores reales de los controles. Los spans de
                // estos labels se recrean con innerHTML al cambiar de modo.
                const currentBgValue = bgRemovalInput.value;
                const currentThresholdValue = thresholdInput.value;

                if (mode === 'dark') {
                    // MODO OSCURO (sin cambios)
                    bgRemovalLabel.innerHTML = `
                        Tolerancia de Eliminación de Negro
                        <span class="value-display" id="bgRemovalValue">${currentBgValue}</span>
                    `;
                    bgRemovalHelp.textContent = 'Elimina el fondo negro. Valores bajos = más preciso.';
                    
                    thresholdLabel.innerHTML = `
                        Umbral de Protección de Luces
                        <span class="value-display" id="thresholdValue">${currentThresholdValue}</span>
                    `;
                    thresholdHelp.textContent = 'Las áreas más claras que este valor no se perforan (quedan como tinta sólida).';
                    
                    garmentModeHelp.textContent = 'Oscura: Las sombras revelan la tela negra, las luces son tinta sólida.';
                    // Ocultar controles de prenda personalizada
                    customGarmentColorGroup.style.display = 'none';
                    garmentColorBgBtn.style.display = 'none';

                } else if (mode === 'light') {
                    // MODO CLARO (sin cambios)
                    bgRemovalLabel.innerHTML = `
                        Tolerancia de Eliminación de Blanco
                        <span class="value-display" id="bgRemovalValue">${currentBgValue}</span>
                    `;
                    bgRemovalHelp.textContent = 'Elimina el fondo blanco puro. Valores bajos = solo blanco absoluto.';
                    
                    thresholdLabel.innerHTML = `
                        Umbral de Protección de Oscuros
                        <span class="value-display" id="thresholdValue">${currentThresholdValue}</span>
                    `;
                    thresholdHelp.textContent = 'Las áreas más oscuras que este valor no se perforan (quedan como tinta sólida).';
                    
                    garmentModeHelp.textContent = 'Clara: Las luces revelan la tela blanca, los oscuros son tinta sólida.';
                    // Ocultar controles de prenda personalizada
                    customGarmentColorGroup.style.display = 'none';
                    garmentColorBgBtn.style.display = 'none';

                } else if (mode === 'custom') {
                    // ── MODO PERSONALIZADO: Distancia de color ──────────────────
                    customGarmentColorGroup.style.display = 'block';
                    garmentColorBgBtn.style.display = 'inline-flex';

                    bgRemovalLabel.innerHTML = `
                        Tolerancia de Eliminación de Color de Prenda
                        <span class="value-display" id="bgRemovalValue">${currentBgValue}</span>
                    `;
                    bgRemovalHelp.textContent = 'Elimina píxeles similares al color de la prenda. Valores bajos = solo el color exacto.';

                    thresholdLabel.innerHTML = `
                        Umbral de Protección de Colores Diferentes
                        <span class="value-display" id="thresholdValue">${currentThresholdValue}</span>
                    `;
                    thresholdHelp.textContent = 'Los colores muy distintos al de la prenda no se perforan (quedan como tinta sólida).';

                    garmentModeHelp.textContent = 'Personalizado: Similar al color de prenda → tela visible. Diferente → tinta sólida.';
                }
            }

            // ═══════════════════════════════════════════════════════════════════
            // TURNSTILE CALLBACKS (NUEVO)
            // ═══════════════════════════════════════════════════════════════════

            window.onTurnstileSuccess = function(token) {
                enhancerState.turnstileToken = token;
                if (enhancerState.turnstileTimeout) {
                    clearTimeout(enhancerState.turnstileTimeout);
                    enhancerState.turnstileTimeout = null;
                }
                if (enhancerState.pendingTurnstileResolve) {
                    enhancerState.pendingTurnstileResolve(token);
                    enhancerState.pendingTurnstileResolve = null;
                    enhancerState.pendingTurnstileReject = null;
                }
                updateImprovementButtonState();
            };

            window.onTurnstileExpired = function() {
                enhancerState.turnstileToken = null;
                updateImprovementButtonState();
            };

            window.onTurnstileError = function() {
                enhancerState.turnstileToken = null;
                if (enhancerState.turnstileTimeout) {
                    clearTimeout(enhancerState.turnstileTimeout);
                    enhancerState.turnstileTimeout = null;
                }
                if (enhancerState.pendingTurnstileReject) {
                    enhancerState.pendingTurnstileReject(new Error('No se pudo validar Turnstile. Recarga la página e intenta de nuevo.'));
                    enhancerState.pendingTurnstileResolve = null;
                    enhancerState.pendingTurnstileReject = null;
                }
                updateImprovementButtonState();
            };

            function resetTurnstile() {
                enhancerState.turnstileToken = null;
                if (enhancerState.turnstileTimeout) {
                    clearTimeout(enhancerState.turnstileTimeout);
                    enhancerState.turnstileTimeout = null;
                }
                enhancerState.pendingTurnstileResolve = null;
                enhancerState.pendingTurnstileReject = null;
                if (window.turnstile) {
                    try { turnstile.reset('#turnstile-widget'); } catch (error) { console.warn('No se pudo resetear Turnstile:', error); }
                }
                updateImprovementButtonState();
            }

            function waitForTurnstileReady(timeoutMs = 8000) {
                return new Promise((resolve, reject) => {
                    const startedAt = Date.now();
                    const check = () => {
                        if (window.turnstile && typeof turnstile.execute === 'function') {
                            resolve();
                            return;
                        }
                        if (Date.now() - startedAt > timeoutMs) {
                            reject(new Error('Turnstile no cargó correctamente. Revisa conexión, bloqueadores o dominio permitido.'));
                            return;
                        }
                        setTimeout(check, 120);
                    };
                    check();
                });
            }

            async function ensureTurnstileToken() {
                if (enhancerState.turnstileToken) return enhancerState.turnstileToken;
                await waitForTurnstileReady();

                return new Promise((resolve, reject) => {
                    enhancerState.pendingTurnstileResolve = resolve;
                    enhancerState.pendingTurnstileReject = reject;
                    enhancerState.turnstileTimeout = setTimeout(() => {
                        enhancerState.pendingTurnstileResolve = null;
                        enhancerState.pendingTurnstileReject = null;
                        enhancerState.turnstileTimeout = null;
                        reject(new Error('Turnstile no generó token. Verifica que el dominio esté permitido en Cloudflare.'));
                    }, 12000);

                    try {
                        turnstile.execute('#turnstile-widget');
                    } catch (error) {
                        if (enhancerState.turnstileTimeout) clearTimeout(enhancerState.turnstileTimeout);
                        enhancerState.turnstileTimeout = null;
                        enhancerState.pendingTurnstileResolve = null;
                        enhancerState.pendingTurnstileReject = null;
                        reject(error);
                    }
                });
            }

            // ═══════════════════════════════════════════════════════════════════
            // FUNCIONES DE MEJORA UNIFICADA (NUEVO)
            // ═══════════════════════════════════════════════════════════════════

            // Distancia RGB normalizada a 0-255 (reutilizada de semitonos para transparencia IA)
            function getColorDistNorm(r, g, b, tR, tG, tB) {
                const dr = r - tR, dg = g - tG, db = b - tB;
                const dist = Math.sqrt(dr * dr + dg * dg + db * db);
                return Math.min(255, dist * 255 / 441.67);
            }

            // Extraer canal alfa como imagen de luminancia (reutilizado del mejorador)
            function extractAlphaChannel(file) {
                return new Promise((resolve) => {
                    const img = new Image();
                    img.onload = () => {
                        const c = document.createElement('canvas');
                        c.width = img.width;
                        c.height = img.height;
                        const ctx = c.getContext('2d');
                        ctx.drawImage(img, 0, 0);
                        const data = ctx.getImageData(0, 0, c.width, c.height).data;
                        
                        let hasTransparency = false;
                        for (let i = 3; i < data.length; i += 4) {
                            if (data[i] < 250) { hasTransparency = true; break; }
                        }
                        
                        if (!hasTransparency) { resolve(null); return; }
                        
                        const alphaCanvas = document.createElement('canvas');
                        alphaCanvas.width = img.width;
                        alphaCanvas.height = img.height;
                        const alphaCtx = alphaCanvas.getContext('2d');
                        const alphaImg = alphaCtx.createImageData(img.width, img.height);
                        for (let i = 0; i < data.length; i += 4) {
                            const a = data[i + 3];
                            alphaImg.data[i] = a;
                            alphaImg.data[i + 1] = a;
                            alphaImg.data[i + 2] = a;
                            alphaImg.data[i + 3] = 255;
                        }
                        alphaCtx.putImageData(alphaImg, 0, 0);
                        resolve(alphaCanvas);
                        URL.revokeObjectURL(img.src);
                    };
                    img.src = URL.createObjectURL(file);
                });
            }

            // Re-aplicar transparencia a imagen mejorada (reutilizado del mejorador)
            function applyAlphaToResult(resultImg, alphaCanvas) {
                return new Promise((resolve) => {
                    const w = resultImg.width;
                    const h = resultImg.height;
                    
                    const scaledAlpha = document.createElement('canvas');
                    scaledAlpha.width = w;
                    scaledAlpha.height = h;
                    const scCtx = scaledAlpha.getContext('2d');
                    scCtx.imageSmoothingEnabled = true;
                    scCtx.imageSmoothingQuality = 'high';
                    scCtx.drawImage(alphaCanvas, 0, 0, w, h);
                    const alphaData = scCtx.getImageData(0, 0, w, h).data;
                    
                    const finalCanvas = document.createElement('canvas');
                    finalCanvas.width = w;
                    finalCanvas.height = h;
                    const fCtx = finalCanvas.getContext('2d');
                    fCtx.drawImage(resultImg, 0, 0);
                    const finalData = fCtx.getImageData(0, 0, w, h);
                    
                    for (let i = 0; i < finalData.data.length; i += 4) {
                        finalData.data[i + 3] = alphaData[i];
                    }
                    
                    fCtx.putImageData(finalData, 0, 0);
                    
                    finalCanvas.toBlob((blob) => {
                        const url = URL.createObjectURL(blob);
                        resolve(url);
                    }, 'image/png');
                });
            }

            async function finalUrlToFile(url, fileName) {
                try {
                    const response = await fetch(url);
                    const blob = await response.blob();
                    return new File([blob], fileName, { type: blob.type || 'image/png' });
                } catch (error) {
                    console.warn('No se pudo actualizar el archivo interno mejorado:', error);
                    return null;
                }
            }

            // Mejorar la imagen y recargarla en el lienzo original (NUEVO)
            async function improveAndReloadImage() {
                if (!enhancerState.currentFile || enhancerState.isProcessingEnhancement) return;

                enhancerState.isProcessingEnhancement = true;
                updateImprovementButtonState();
                processingMessage.style.color = '#E3FC02';
                processingMessage.textContent = 'Mejorando imagen... (1/4 Validando)';

                try {
                    const turnstileToken = await ensureTurnstileToken();

                    // 1. Extraer canal alfa ANTES de enviar
                    processingMessage.textContent = 'Mejorando imagen... (2/4 Transparencia)';
                    const alphaCanvas = await extractAlphaChannel(enhancerState.currentFile);

                    processingMessage.textContent = 'Mejorando imagen... (3/4 Procesando IA)';
                    const formData = new FormData();
                    formData.append('image', enhancerState.currentFile);

                    // 2. Enviar al worker
                    const response = await fetch(ENHANCER_CONFIG.WORKER_URL, {
                        method: 'POST',
                        headers: { 'CF-Turnstile-Token': turnstileToken },
                        body: formData
                    });

                    const rawResponse = await response.text();
                    let result = null;
                    try {
                        result = rawResponse ? JSON.parse(rawResponse) : null;
                    } catch (parseError) {
                        throw new Error('El servidor del mejorador no devolvió JSON válido. Respuesta: ' + rawResponse.slice(0, 120));
                    }

                    if (!response.ok || !result || !result.success) {
                        let errorMsg = result?.error || `Error HTTP ${response.status}`;
                        if (result?.retry_after) errorMsg += `. Intenta en ${result.retry_after} segundos`;
                        throw new Error(errorMsg);
                    }

                    processingMessage.textContent = 'Mejorando imagen... (4/4 Reaplicando Transparencia)';

                    // 3. Cargar resultado y reaplicar alfa
                    const resultImg = new Image();
                    resultImg.crossOrigin = 'anonymous';
                    
                    await new Promise((resolve, reject) => {
                        resultImg.onload = resolve;
                        resultImg.onerror = () => reject(new Error('Error al cargar imagen mejorada'));
                        resultImg.src = result.output_url;
                    });

                    // Re-aplicar transparencia si la imagen original la tenía
                    let finalUrl = result.output_url;
                    if (alphaCanvas) {
                        finalUrl = await applyAlphaToResult(resultImg, alphaCanvas);
                    }

                    processingMessage.textContent = 'Imagen mejorada. Recargando...';

                    // 4. Cargar la imagen mejorada final en el lienzo original
                    const improvedImg = new Image();
                    
                    // --- SOLUCIÓN 1: Prevenir error de CORS que deja el lienzo en blanco ---
                    improvedImg.crossOrigin = 'anonymous'; 
                    
                    improvedImg.onload = () => {
                        // Reemplazar la imagen actual
                        currentImage = improvedImg;
                        finalUrlToFile(finalUrl, originalFileName + '_mejorada.png').then((file) => {
                            if (file) enhancerState.currentFile = file;
                        });
                        invalidatePreparedSourceCache();
                        outputCanvas.width = improvedImg.width;
                        outputCanvas.height = improvedImg.height;
                        outputCtx.clearRect(0, 0, improvedImg.width, improvedImg.height);
                        outputCtx.imageSmoothingEnabled = true;
                        outputCtx.imageSmoothingQuality = 'high';
                        outputCtx.drawImage(improvedImg, 0, 0);
                        updateDownloadSizeFieldsFromSourceImage();

                        // Mostrar la imagen inmediatamente y procesar el semitono sin dejar el lienzo vacío.
                        processImage(0);
                        
                        processingMessage.textContent = 'Imagen mejorada y cargada exitosamente.';
                        setTimeout(() => { processingMessage.textContent = ''; }, 3000);

                        // Ocultar asistente de mejora (mismo flujo que carga inicial)
                        enhancerControlsGroup.style.display = 'none';
                        imageUpload.value = ''; // Limpiar input file
                        
                        // --- SOLUCIÓN 2: Reajustar la vista a la nueva resolución de la IA ---
                        requestAnimationFrame(() => {
                            resetViewBtn.click();
                        });
                    };
                    improvedImg.onerror = () => {
                        processingMessage.textContent = 'Error al cargar la imagen en el lienzo.';
                        processingMessage.style.color = '#ff4757';
                        throw new Error('Error al recargar la imagen mejorada final.');
                    };
                    improvedImg.src = finalUrl;

                } catch (error) {
                    console.error('Error durante la mejora unificada:', error);
                    processingMessage.textContent = 'Error durante la mejora. Intenta nuevamente.';
                    processingMessage.style.color = '#ff4757';
                    setTimeout(() => { 
                        processingMessage.textContent = ''; 
                        processingMessage.style.color = '#E3FC02';
                    }, 5000);
                } finally {
                    enhancerState.isProcessingEnhancement = false;
                    startCooldown();
                    resetTurnstile();
                }
            }

            function updateImprovementButtonState() {
                const canProcess = enhancerState.currentFile && !enhancerState.isProcessingEnhancement && enhancerState.cooldownRemaining <= 0;
                improveImageBtn.disabled = !canProcess;
                
                if (enhancerState.cooldownRemaining > 0) {
                    improveImageBtn.textContent = `Espera ${enhancerState.cooldownRemaining}s`;
                } else if (!enhancerState.isProcessingEnhancement) {
                    improveImageBtn.textContent = enhancerState.currentFile ? 'Mejorar Imagen con IA' : 'Carga una imagen primero';
                } else {
                    improveImageBtn.textContent = 'Procesando...';
                }
            }

            function startCooldown() {
                enhancerState.cooldownRemaining = ENHANCER_CONFIG.COOLDOWN_SECONDS;
                updateImprovementButtonState();
                
                const timer = setInterval(() => {
                    enhancerState.cooldownRemaining--;
                    updateImprovementButtonState();
                    if (enhancerState.cooldownRemaining <= 0) {
                        clearInterval(timer);
                    }
                }, 1000);
            }

            // ═══════════════════════════════════════════════════════════════════
            // EVENT LISTENERS
            // ═══════════════════════════════════════════════════════════════════

            improveImageBtn.addEventListener('click', improveAndReloadImage);

            garmentModeSelect.addEventListener('change', () => {
                updateUIForMode();
                if (currentImage) processImage();
            });

            zoomInput.addEventListener('input', (e) => {
                const newZoom = parseInt(e.target.value);
                const cx = canvasContainer.clientWidth / 2;
                const cy = canvasContainer.clientHeight / 2;
                const scaleFactor = newZoom / currentZoom;
                panX = cx - scaleFactor * (cx - panX);
                panY = cy - scaleFactor * (cy - panY);
                currentZoom = newZoom;
                zoomValue.textContent = newZoom + '%';
                applyTransform();
            });

            canvasContainer.addEventListener('wheel', (e) => {
                e.preventDefault();
                const zoomStep = 10;
                const newZoom = e.deltaY < 0
                    ? Math.min(300, currentZoom + zoomStep)
                    : Math.max(10, currentZoom - zoomStep);
                const rect = canvasContainer.getBoundingClientRect();
                const mouseX = e.clientX - rect.left;
                const mouseY = e.clientY - rect.top;
                const scaleFactor = newZoom / currentZoom;
                panX = mouseX - scaleFactor * (mouseX - panX);
                panY = mouseY - scaleFactor * (mouseY - panY);
                currentZoom = newZoom;
                zoomInput.value = newZoom;
                zoomValue.textContent = newZoom + '%';
                applyTransform();
            });

            canvasContainer.addEventListener('mousedown', (e) => {
                if (!currentImage) return;

                // Clic derecho: mover la imagen incluso cuando el pincel está activo.
                // Se procesa antes que el cuentagotas y el pincel para evitar trazos accidentales.
                if (e.button === 2) {
                    e.preventDefault();
                    isBrushDrawing = false;
                    lastBrushPoint = null;
                    isPanning = true;
                    lastX = e.clientX;
                    lastY = e.clientY;
                    brushCursor.style.display = 'none';
                    canvasContainer.classList.add('grabbing');
                    return;
                }

                if (e.button !== 0) return;

                const point = getCanvasPointFromClient(e.clientX, e.clientY);

                if (activeColorPickerIndex >= 0) {
                    if (!isPointInsideCanvas(point)) return;
                    const pixel = originalCtx.getImageData(Math.floor(point.x), Math.floor(point.y), 1, 1).data;
                    const slots = getProtectedColorSlots();
                    const activeSlot = slots[activeColorPickerIndex];
                    if (!activeSlot) {
                        activeColorPickerIndex = -1;
                        canvasContainer.classList.remove('color-pick-mode');
                        return;
                    }
                    activeSlot.querySelector('.protected-color-input').value = rgbToHex(pixel[0], pixel[1], pixel[2]);
                    activeSlot.querySelector('.protected-color-enabled').checked = true;
                    protectColorsEnabled.checked = true;
                    getProtectedColorPickerButtons().forEach((button) => button.classList.remove('active'));
                    activeColorPickerIndex = -1;
                    canvasContainer.classList.remove('color-pick-mode');
                    processImage();
                    return;
                }

                if (isRestoreBrushActive) {
                    if (!isPointInsideCanvas(point)) return;
                    isBrushDrawing = true;
                    lastBrushPoint = point;
                    drawRestoreStroke(point, point);
                    return;
                }

                isPanning = true;
                lastX = e.clientX;
                lastY = e.clientY;
                canvasContainer.classList.add('grabbing');
            });

            canvasContainer.addEventListener('mousemove', (e) => {
                // Mientras se desplaza con clic derecho, priorizar el paneo y ocultar el cursor del pincel.
                if (isPanning) {
                    brushCursor.style.display = 'none';
                    panX += e.clientX - lastX;
                    panY += e.clientY - lastY;
                    lastX = e.clientX;
                    lastY = e.clientY;
                    applyTransform();
                    return;
                }

                updateBrushCursor(e.clientX, e.clientY);

                if (isBrushDrawing && isRestoreBrushActive) {
                    const point = getCanvasPointFromClient(e.clientX, e.clientY);
                    if (isPointInsideCanvas(point)) {
                        drawRestoreStroke(lastBrushPoint, point);
                        lastBrushPoint = point;
                    }
                }
            });

            function finishCanvasInteraction() {
                // FIX v2.1: en modo "Tramado profesional" los puntos usan alfa parcial
                // (semitransparencia) para conservar color en zonas claras/dispersas.
                // Antes esto se binarizaba (0/255) tras usar el pincel restaurador,
                // borrando ese matiz igual que ocurría al descargar.
                if (isBrushDrawing && hardEdgesCheckbox.checked) {
                    applyHardEdges(outputCtx, outputCanvas.width, outputCanvas.height);
                }
                isBrushDrawing = false;
                lastBrushPoint = null;
                isPanning = false;
                canvasContainer.classList.remove('grabbing');
            }

            canvasContainer.addEventListener('mouseup', finishCanvasInteraction);
            window.addEventListener('mouseup', finishCanvasInteraction);

            canvasContainer.addEventListener('mouseleave', () => {
                brushCursor.style.display = 'none';
                finishCanvasInteraction();
            });

            // Bloquear menú contextual en el área de previsualización
            canvasContainer.addEventListener('contextmenu', (e) => e.preventDefault());
            outputCanvas.addEventListener('contextmenu', (e) => e.preventDefault());

            resetViewBtn.addEventListener('click', () => {
                if (!currentImage) return;
                const containerWidth = canvasContainer.clientWidth;
                const containerHeight = canvasContainer.clientHeight;
                const contentWidth = outputCanvas.width;
                const contentHeight = outputCanvas.height;
                let newScale = Math.min(containerWidth / contentWidth, containerHeight / contentHeight, 1.0) * 0.90;
                if (newScale < 0.05) newScale = 0.05;
                const newZoomPercent = Math.round(newScale * 100);
                currentZoom = newZoomPercent;
                zoomInput.value = newZoomPercent;
                zoomValue.textContent = newZoomPercent + '%';
                // Centrar la imagen en el contenedor
                panX = (containerWidth - contentWidth * newScale) / 2;
                panY = (containerHeight - contentHeight * newScale) / 2;
                applyTransform();
            });

            const bgButtons = document.querySelectorAll('.bg-btn');
            const customBgColorInput = document.getElementById('customBgColor');
            const customColorBtn = document.querySelector('.bg-btn.custom-color');

            bgButtons.forEach(btn => {
                btn.addEventListener('click', () => {
                    bgButtons.forEach(b => b.classList.remove('active'));
                    btn.classList.add('active');
                    const bg = btn.dataset.bg;
                    if (bg === 'transparent' || bg === 'transparent-white') {
                        canvasContainer.style.backgroundImage = '';
                        canvasContainer.style.backgroundColor = '';
                        canvasContainer.classList.remove('checkerboard-bg', 'checkerboard-bg-white');
                        canvasContainer.classList.add(
                            bg === 'transparent' ? 'checkerboard-bg' : 'checkerboard-bg-white'
                        );
                    } else if (bg === 'custom') {
                        // Usar el color actual del input
                        const customColor = customBgColorInput.value;
                        canvasContainer.classList.remove('checkerboard-bg', 'checkerboard-bg-white');
                        canvasContainer.style.backgroundImage = 'none';
                        canvasContainer.style.backgroundColor = customColor;
                    } else if (bg === 'garment-custom') {
                        // Fondo sincronizado con el color de prenda personalizado (NUEVO)
                        const garmentColor = customGarmentColorInput.value;
                        canvasContainer.classList.remove('checkerboard-bg', 'checkerboard-bg-white');
                        canvasContainer.style.backgroundImage = 'none';
                        canvasContainer.style.backgroundColor = garmentColor;
                    } else {
                        canvasContainer.classList.remove('checkerboard-bg', 'checkerboard-bg-white');
                        canvasContainer.style.backgroundImage = 'none';
                        canvasContainer.style.backgroundColor = bg;
                    }
                });
            });

            // Manejar cambio de color personalizado
            customBgColorInput.addEventListener('input', (e) => {
                const newColor = e.target.value;
                customColorBtn.style.backgroundColor = newColor;
                
                // Si el botón custom está activo, actualizar el fondo inmediatamente
                if (customColorBtn.classList.contains('active')) {
                    canvasContainer.classList.remove('checkerboard-bg', 'checkerboard-bg-white');
                    canvasContainer.style.backgroundImage = 'none';
                    canvasContainer.style.backgroundColor = newColor;
                }
            });

            // ── NUEVO: Sincronizar color de prenda personalizado con botón de fondo ──
            customGarmentColorInput.addEventListener('input', (e) => {
                const newGarmentColor = e.target.value;
                // Actualizar color visible del botón de fondo sincronizado
                garmentColorBgBtn.style.backgroundColor = newGarmentColor;
                // Si ese botón está activo como fondo, actualizar el canvas de inmediato
                if (garmentColorBgBtn.classList.contains('active')) {
                    canvasContainer.classList.remove('checkerboard-bg', 'checkerboard-bg-white');
                    canvasContainer.style.backgroundImage = 'none';
                    canvasContainer.style.backgroundColor = newGarmentColor;
                }
                // Actualizar textos de ayuda (pueden variar según nueva luminosidad)
                updateUIForMode();
                // Reprocesar si hay imagen cargada
                if (currentImage) processImage();
            });

            // Activar el botón custom al hacer click en el color picker
            customBgColorInput.addEventListener('click', (e) => {
                e.stopPropagation();
                bgButtons.forEach(b => b.classList.remove('active'));
                customColorBtn.classList.add('active');
                canvasContainer.classList.remove('checkerboard-bg', 'checkerboard-bg-white');
                canvasContainer.style.backgroundImage = 'none';
                canvasContainer.style.backgroundColor = customBgColorInput.value;
            });

            protectColorsEnabled.addEventListener('change', () => {
                if (currentImage) processImage();
            });

            protectedColorsGrid.addEventListener('input', (e) => {
                if (!e.target.classList.contains('protected-color-input')) return;
                protectColorsEnabled.checked = true;
                if (currentImage) processImage();
            });

            protectedColorsGrid.addEventListener('change', (e) => {
                if (!e.target.classList.contains('protected-color-enabled')) return;
                protectColorsEnabled.checked = true;
                if (currentImage) processImage();
            });

            protectedColorsGrid.addEventListener('click', (e) => {
                const picker = e.target.closest('.color-picker-btn');
                const removeButton = e.target.closest('.remove-protected-color-btn');

                if (picker) {
                    const buttons = getProtectedColorPickerButtons();
                    const index = buttons.indexOf(picker);
                    const activating = activeColorPickerIndex !== index;
                    buttons.forEach((item) => item.classList.remove('active'));
                    activeColorPickerIndex = activating ? index : -1;
                    picker.classList.toggle('active', activating);
                    canvasContainer.classList.toggle('color-pick-mode', activating);
                    if (activating && isRestoreBrushActive) {
                        isRestoreBrushActive = false;
                        restoreBrushBtn.classList.remove('active');
                        canvasContainer.classList.remove('brush-mode');
                        brushCursor.style.display = 'none';
                    }
                    return;
                }

                if (removeButton) {
                    const slots = getProtectedColorSlots();
                    if (slots.length <= 1) return;
                    const slot = removeButton.closest('.protected-color-slot');
                    const removedIndex = slots.indexOf(slot);
                    slot.remove();
                    if (activeColorPickerIndex === removedIndex) {
                        activeColorPickerIndex = -1;
                        canvasContainer.classList.remove('color-pick-mode');
                    } else if (activeColorPickerIndex > removedIndex) {
                        activeColorPickerIndex--;
                    }
                    updateProtectedColorUI();
                    if (currentImage) processImage();
                }
            });

            addProtectedColorBtn.addEventListener('click', () => {
                const slots = getProtectedColorSlots();
                if (slots.length >= 5) return;
                const palette = ['#ffffff', '#000000', '#808080', '#e3fc02'];
                protectedColorsGrid.appendChild(createProtectedColorSlot(palette[(slots.length - 1) % palette.length]));
                protectColorsEnabled.checked = true;
                updateProtectedColorUI();
                if (currentImage) processImage();
            });

            updateProtectedColorUI();

            protectedColorToleranceInput.addEventListener('input', (e) => {
                protectedColorToleranceValue.textContent = e.target.value;
                if (currentImage) processImage();
            });

            restoreBrushBtn.addEventListener('click', () => {
                isRestoreBrushActive = !isRestoreBrushActive;
                restoreBrushBtn.classList.toggle('active', isRestoreBrushActive);
                canvasContainer.classList.toggle('brush-mode', isRestoreBrushActive);
                if (!isRestoreBrushActive) brushCursor.style.display = 'none';

                if (isRestoreBrushActive && activeColorPickerIndex >= 0) {
                    activeColorPickerIndex = -1;
                    getProtectedColorPickerButtons().forEach((button) => button.classList.remove('active'));
                    canvasContainer.classList.remove('color-pick-mode');
                }
            });

            restoreBrushSizeInput.addEventListener('input', (e) => {
                restoreBrushSizeValue.textContent = `${e.target.value} px`;
            });

            clearBrushBtn.addEventListener('click', () => {
                manualRestoreMaskCtx.clearRect(0, 0, manualRestoreMaskCanvas.width, manualRestoreMaskCanvas.height);
                if (currentImage) processImage();
            });

            bgRemovalInput.addEventListener('input', (e) => {
                document.getElementById('bgRemovalValue').textContent = e.target.value;
                if (currentImage) processImage();
            });

            thresholdInput.addEventListener('input', (e) => {
                document.getElementById('thresholdValue').textContent = e.target.value;
                if (currentImage) processImage();
            });

            separationInput.addEventListener('input', (e) => {
                separationValue.textContent = e.target.value;
                if (currentImage) processImage();
            });

            maxSizeInput.addEventListener('input', (e) => {
                maxSizeValue.textContent = parseFloat(e.target.value).toFixed(1);
                if (currentImage) processImage();
            });

            screenAngleInput.addEventListener('input', (e) => {
                screenAngleValue.textContent = e.target.value + '°';
                if (currentImage) processImage();
            });

            gammaInput.addEventListener('input', (e) => {
                gammaValue.textContent = parseFloat(e.target.value).toFixed(1);
                if (currentImage) processImage();
            });

            dotShapeSelect.addEventListener('change', () => {
                if (currentImage) processImage();
            });

            hardEdgesCheckbox.addEventListener('change', () => {
                if (currentImage) processImage();
            });

            exportWidthInput.addEventListener('input', () => {
                syncExportMeasure('width');
                processImageFromCurrentMeasures(220);
            });
            exportWidthInput.addEventListener('change', () => processImageFromCurrentMeasures(0));

            exportHeightInput.addEventListener('input', () => {
                syncExportMeasure('height');
                processImageFromCurrentMeasures(220);
            });
            exportHeightInput.addEventListener('change', () => processImageFromCurrentMeasures(0));

            exportUnitSelect.addEventListener('change', () => {
                updateDownloadSizeFieldsFromCanvas();
                processImageFromCurrentMeasures(0);
            });

            exportDpiInput.addEventListener('input', () => {
                if (exportUnitSelect.value !== 'px') updateDownloadSizeFieldsFromCanvas();
                processImageFromCurrentMeasures(220);
            });
            exportDpiInput.addEventListener('change', () => processImageFromCurrentMeasures(0));
            lockExportRatioCheckbox.addEventListener('change', () => {
                if (lockExportRatioCheckbox.checked) {
                    syncExportMeasure('width');
                } else {
                    updateExportAspectRatioFromCurrentMeasures();
                }
                processImageFromCurrentMeasures(0);
            });

            // ═══════════════════════════════════════════════════════════════════
            // SOPORTE TÁCTIL: pan con un dedo + pinch-to-zoom con dos dedos
            // ═══════════════════════════════════════════════════════════════════

            let touchMode = null;          // 'pan' | 'pinch' | null
            let touchStartDist = 0;
            let touchStartZoom = 100;
            let touchStartPanX = 0;
            let touchStartPanY = 0;
            let touchAnchorX = 0;
            let touchAnchorY = 0;
            let lastTouchX = 0;
            let lastTouchY = 0;

            function getTouchDistance(t1, t2) {
                const dx = t2.clientX - t1.clientX;
                const dy = t2.clientY - t1.clientY;
                return Math.sqrt(dx * dx + dy * dy);
            }

            canvasContainer.addEventListener('touchstart', (e) => {
                if (e.touches.length === 0 || isRestoreBrushActive || activeColorPickerIndex >= 0) return;
                e.preventDefault();
                const rect = canvasContainer.getBoundingClientRect();

                if (e.touches.length === 1) {
                    touchMode = 'pan';
                    lastTouchX = e.touches[0].clientX;
                    lastTouchY = e.touches[0].clientY;
                } else {
                    touchMode = 'pinch';
                    touchStartDist = getTouchDistance(e.touches[0], e.touches[1]);
                    touchStartZoom = currentZoom;
                    touchStartPanX = panX;
                    touchStartPanY = panY;
                    touchAnchorX = (e.touches[0].clientX + e.touches[1].clientX) / 2 - rect.left;
                    touchAnchorY = (e.touches[0].clientY + e.touches[1].clientY) / 2 - rect.top;
                }
            }, { passive: false });

            canvasContainer.addEventListener('touchmove', (e) => {
                if (touchMode === null || isRestoreBrushActive || activeColorPickerIndex >= 0) return;
                e.preventDefault();
                const rect = canvasContainer.getBoundingClientRect();

                if (touchMode === 'pan' && e.touches.length === 1) {
                    panX += e.touches[0].clientX - lastTouchX;
                    panY += e.touches[0].clientY - lastTouchY;
                    lastTouchX = e.touches[0].clientX;
                    lastTouchY = e.touches[0].clientY;
                    applyTransform();
                } else if (touchMode === 'pinch' && e.touches.length >= 2) {
                    const newDist = getTouchDistance(e.touches[0], e.touches[1]);
                    if (touchStartDist <= 0) return;

                    let newZoom = Math.round(touchStartZoom * (newDist / touchStartDist));
                    newZoom = Math.max(10, Math.min(300, newZoom));

                    const midX = (e.touches[0].clientX + e.touches[1].clientX) / 2 - rect.left;
                    const midY = (e.touches[0].clientY + e.touches[1].clientY) / 2 - rect.top;
                    const scaleFactor = newZoom / touchStartZoom;

                    // Zoom anclado al punto inicial + desplazamiento del midpoint actual
                    panX = touchAnchorX - scaleFactor * (touchAnchorX - touchStartPanX) + (midX - touchAnchorX);
                    panY = touchAnchorY - scaleFactor * (touchAnchorY - touchStartPanY) + (midY - touchAnchorY);

                    currentZoom = newZoom;
                    zoomInput.value = newZoom;
                    zoomValue.textContent = newZoom + '%';
                    applyTransform();
                }
            }, { passive: false });

            canvasContainer.addEventListener('touchend', (e) => {
                if (e.touches.length === 0) {
                    touchMode = null;
                } else if (e.touches.length === 1) {
                    // Transición pinch → pan: reiniciar referencias del dedo restante
                    touchMode = 'pan';
                    lastTouchX = e.touches[0].clientX;
                    lastTouchY = e.touches[0].clientY;
                }
            });

            canvasContainer.addEventListener('touchcancel', () => {
                touchMode = null;
            });

            // ═══════════════════════════════════════════════════════════════════
            // FUNCIONES DE CARGA DE IMAGEN (MEJORADA UNIFICADA)
            // Soporta: Click, Drag & Drop, Ctrl+V (Paste)
            // ═══════════════════════════════════════════════════════════════════

            function loadImageFromFile(file) {
                if (!file || !file.type.startsWith('image/')) {
                    console.warn('Archivo no válido:', file?.type);
                    return;
                }

                originalFileName = file.name.replace(/\.[^/.]+$/, '') || 'imagen';
                enhancerState.currentFile = file;

                const objectUrl = URL.createObjectURL(file);
                const img = new Image();
                img.decoding = 'async';

                img.onload = () => {
                    URL.revokeObjectURL(objectUrl);
                    currentImage = img;
                    invalidatePreparedSourceCache();

                    outputCanvas.width = img.width;
                    outputCanvas.height = img.height;
                    outputCtx.clearRect(0, 0, img.width, img.height);
                    outputCtx.imageSmoothingEnabled = true;
                    outputCtx.imageSmoothingQuality = 'high';
                    outputCtx.drawImage(img, 0, 0);

                    updateDownloadSizeFieldsFromSourceImage();
                    downloadBtn.disabled = false;
                    canvasContainer.style.display = 'block';

                    requestAnimationFrame(() => {
                        resetViewBtn.click();
                        processImage(0);
                    });

                    enhancerControlsGroup.style.display = 'block';
                    resetTurnstile();
                    updateImprovementButtonState();
                };

                img.onerror = () => {
                    URL.revokeObjectURL(objectUrl);
                    console.error('Error al cargar la imagen');
                };

                img.src = objectUrl;
            }

            // 1. CLICK - Input file tradicional
            imageUpload.addEventListener('change', (e) => {
                const file = e.target.files[0];
                if (file) loadImageFromFile(file);
            });

            // 2. DRAG & DROP - Zona de upload
            uploadZone.addEventListener('dragover', (e) => {
                e.preventDefault();
                e.stopPropagation();
                uploadZone.classList.add('drag-over');
            });

            uploadZone.addEventListener('dragleave', (e) => {
                e.preventDefault();
                e.stopPropagation();
                uploadZone.classList.remove('drag-over');
            });

            uploadZone.addEventListener('drop', (e) => {
                e.preventDefault();
                e.stopPropagation();
                uploadZone.classList.remove('drag-over');
                
                const file = e.dataTransfer.files[0];
                if (file) loadImageFromFile(file);
            });

            // 3. DRAG & DROP GLOBAL - Desde cualquier parte de la ventana
            let dragCounter = 0;

            document.addEventListener('dragenter', (e) => {
                e.preventDefault();
                dragCounter++;
                if (e.dataTransfer.types.includes('Files')) {
                    globalDropOverlay.classList.add('visible');
                }
            });

            document.addEventListener('dragleave', (e) => {
                e.preventDefault();
                dragCounter--;
                if (dragCounter === 0) {
                    globalDropOverlay.classList.remove('visible');
                }
            });

            document.addEventListener('dragover', (e) => {
                e.preventDefault();
            });

            document.addEventListener('drop', (e) => {
                e.preventDefault();
                dragCounter = 0;
                globalDropOverlay.classList.remove('visible');
                
                const file = e.dataTransfer.files[0];
                if (file && file.type.startsWith('image/')) {
                    loadImageFromFile(file);
                }
            });

            // 4. PASTE - Ctrl+V desde el portapapeles
            document.addEventListener('paste', (e) => {
                const items = e.clipboardData?.items;
                if (!items) return;

                for (let i = 0; i < items.length; i++) {
                    if (items[i].type.startsWith('image/')) {
                        const file = items[i].getAsFile();
                        if (file) {
                            loadImageFromFile(file);
                            break;
                        }
                    }
                }
            });

            // ═══════════════════════════════════════════════════════════════════
            // FUNCIONES DE UTILIDAD (SIN MODIFICAR)
            // ═══════════════════════════════════════════════════════════════════

            function getLuminosityRec709(r, g, b) {
                return 0.2126 * r + 0.7152 * g + 0.0722 * b;
            }

            function applyGammaCorrection(normalizedLum, gamma) {
                return Math.pow(normalizedLum, 1 / gamma);
            }

            function sample3x3(data, centerX, centerY, width, height) {
                let totalLum = 0;
                let count = 0;
                for (let dy = -1; dy <= 1; dy++) {
                    for (let dx = -1; dx <= 1; dx++) {
                        const sx = Math.round(centerX + dx);
                        const sy = Math.round(centerY + dy);
                        if (sx >= 0 && sx < width && sy >= 0 && sy < height) {
                            const index = (sy * width + sx) * 4;
                            totalLum += getLuminosityRec709(data[index], data[index + 1], data[index + 2]);
                            count++;
                        }
                    }
                }
                return count > 0 ? totalLum / count : 255;
            }

            function drawRotatedShape(ctx, shape, cx, cy, size, angleRad) {
                const safeSize = Math.max(0, Number(size) || 0);
                if (safeSize <= 0) return;

                const halfSize = safeSize / 2;
                if (shape === 'circle') {
                    ctx.beginPath();
                    ctx.arc(cx, cy, halfSize, 0, Math.PI * 2);
                    ctx.fill();
                } else if (shape === 'square') {
                    ctx.save();
                    ctx.translate(cx, cy);
                    ctx.rotate(angleRad);
                    ctx.fillRect(-halfSize, -halfSize, safeSize, safeSize);
                    ctx.restore();
                } else if (shape === 'diamond') {
                    ctx.save();
                    ctx.translate(cx, cy);
                    ctx.rotate(angleRad + Math.PI / 4);
                    ctx.fillRect(-halfSize, -halfSize, safeSize, safeSize);
                    ctx.restore();
                }
            }

            function applyHardEdges(ctx, width, height) {
                const imageData = ctx.getImageData(0, 0, width, height);
                const data = imageData.data;
                const threshold = 128;
                for (let i = 0; i < data.length; i += 4) {
                    data[i + 3] = data[i + 3] < threshold ? 0 : 255;
                }
                ctx.putImageData(imageData, 0, 0);
            }

            // ═══════════════════════════════════════════════════════════════════
            // DEGRADADO CONTINUO, CÍRCULOS DEFINIDOS Y FONDO LIMPIO POR TOLERANCIA
            // La trama conserva puntos de tinta con forma repetible que crecen de forma
            // continua y pasan a perforaciones, sin rayas ni píxeles aleatorios.
            // El fondo se elimina por píxel según la tolerancia seleccionada, no solo
            // por conexión al borde. Así los blancos/negros internos también se limpian.
            // ═══════════════════════════════════════════════════════════════════

            function clamp01(value) {
                return Math.max(0, Math.min(1, value));
            }

            function getToneMetric(data, index, garmentMode, tR, tG, tB) {
                // Motor de Semitonos Fáciles: la cobertura se calcula siempre por
                // distancia al color de la prenda/fondo, no por luminosidad.
                let targetR = 0;
                let targetG = 0;
                let targetB = 0;

                if (garmentMode === 'light') {
                    targetR = 255;
                    targetG = 255;
                    targetB = 255;
                } else if (garmentMode === 'custom') {
                    targetR = tR;
                    targetG = tG;
                    targetB = tB;
                }

                return getColorDistNorm(
                    data[index],
                    data[index + 1],
                    data[index + 2],
                    targetR,
                    targetG,
                    targetB
                );
            }

            function isBackgroundCandidate(
                data,
                pixelIndex,
                garmentMode,
                bgTolerance,
                tR,
                tG,
                tB
            ) {
                const dataIndex = pixelIndex * 4;
                if (data[dataIndex + 3] <= 8) return true;

                const metric = getToneMetric(data, dataIndex, garmentMode, tR, tG, tB);
                return metric <= Math.max(0, Number(bgTolerance) || 0);
            }

            function buildConnectedBackgroundMask(
                data,
                width,
                height,
                garmentMode,
                bgTolerance,
                tR,
                tG,
                tB
            ) {
                const pixelCount = width * height;
                const backgroundMask = new Uint8Array(pixelCount);

                if (!pixelCount) return backgroundMask;

                // IMPORTANTE PARA DTF:
                // La eliminación de fondo debe ser global por tolerancia. Si solo se
                // conserva el fondo conectado al borde, los blancos/negros atrapados
                // dentro de letras, huecos o contornos terminan recibiendo tramado.
                // Aquí evaluamos cada píxel de forma individual, como en la versión
                // anterior que sí limpiaba correctamente el fondo.
                for (let pixelIndex = 0; pixelIndex < pixelCount; pixelIndex++) {
                    if (isBackgroundCandidate(data, pixelIndex, garmentMode, bgTolerance, tR, tG, tB)) {
                        backgroundMask[pixelIndex] = 1;
                    }
                }

                return backgroundMask;
            }

            function sampleToneForHalftone(
                data,
                backgroundMask,
                centerX,
                centerY,
                width,
                height,
                garmentMode,
                tR,
                tG,
                tB,
                separation
            ) {
                const radius = Math.max(1, Math.min(3, Math.round(separation * 0.28)));
                const cx = Math.round(centerX);
                const cy = Math.round(centerY);
                let weightedTotal = 0;
                let weightTotal = 0;

                for (let dy = -radius; dy <= radius; dy++) {
                    const sy = cy + dy;
                    if (sy < 0 || sy >= height) continue;

                    for (let dx = -radius; dx <= radius; dx++) {
                        const sx = cx + dx;
                        if (sx < 0 || sx >= width) continue;

                        const pixelIndex = sy * width + sx;
                        const dataIndex = pixelIndex * 4;
                        if (data[dataIndex + 3] <= 8 || backgroundMask[pixelIndex]) continue;

                        // Promedio ponderado suave: estabiliza pequeñas variaciones de la
                        // imagen sin inclinar el resultado hacia una máscara sólida.
                        const weight = 1 / (1 + dx * dx + dy * dy);
                        weightedTotal += getToneMetric(data, dataIndex, garmentMode, tR, tG, tB) * weight;
                        weightTotal += weight;
                    }
                }

                return weightTotal > 0 ? weightedTotal / weightTotal : null;
            }

            function sampleAverageColorForHalftone(
                data,
                backgroundMask,
                centerX,
                centerY,
                width,
                height,
                separation
            ) {
                // FIX v2.1: un radio de hasta 4 px (separation * 0.34) difumina el color
                // de cada punto con el de sus vecinos, aplanando el detalle de la foto y
                // haciendo que puntos solapados se vean como una mancha uniforme en vez
                // de puntos de tinta diferenciados. Se reduce a un muestreo casi puntual.
                const radius = Math.max(1, Math.min(2, Math.round(separation * 0.16)));
                const cx = Math.round(centerX);
                const cy = Math.round(centerY);
                let rTotal = 0;
                let gTotal = 0;
                let bTotal = 0;
                let aTotal = 0;
                let weightTotal = 0;

                for (let dy = -radius; dy <= radius; dy++) {
                    const sy = cy + dy;
                    if (sy < 0 || sy >= height) continue;

                    for (let dx = -radius; dx <= radius; dx++) {
                        const sx = cx + dx;
                        if (sx < 0 || sx >= width) continue;

                        const pixelIndex = sy * width + sx;
                        if (backgroundMask[pixelIndex]) continue;

                        const dataIndex = pixelIndex * 4;
                        const alpha = data[dataIndex + 3];
                        if (alpha <= 8) continue;

                        const weight = 1 / (1 + dx * dx + dy * dy);
                        rTotal += data[dataIndex] * weight;
                        gTotal += data[dataIndex + 1] * weight;
                        bTotal += data[dataIndex + 2] * weight;
                        aTotal += alpha * weight;
                        weightTotal += weight;
                    }
                }

                if (weightTotal <= 0) return null;

                return {
                    r: rTotal / weightTotal,
                    g: gTotal / weightTotal,
                    b: bTotal / weightTotal,
                    a: clamp01((aTotal / weightTotal) / 255)
                };
            }

            function sampleForegroundSupport(
                data,
                backgroundMask,
                centerX,
                centerY,
                width,
                height,
                separation
            ) {
                const radius = Math.max(1, Math.min(4, Math.round(separation * 0.38)));
                const cx = Math.round(centerX);
                const cy = Math.round(centerY);
                let foregroundWeight = 0;
                let weightTotal = 0;

                for (let dy = -radius; dy <= radius; dy++) {
                    const sy = cy + dy;
                    if (sy < 0 || sy >= height) continue;

                    for (let dx = -radius; dx <= radius; dx++) {
                        const sx = cx + dx;
                        if (sx < 0 || sx >= width) continue;

                        const pixelIndex = sy * width + sx;
                        const dataIndex = pixelIndex * 4;
                        const alpha = data[dataIndex + 3];
                        const weight = 1 / (1 + dx * dx + dy * dy);
                        weightTotal += weight;

                        if (!backgroundMask[pixelIndex] && alpha > 8) {
                            foregroundWeight += weight;
                        }
                    }
                }

                return weightTotal > 0 ? foregroundWeight / weightTotal : 0;
            }

            function getContinuousInkCoverage(metric, garmentMode, bgTolerance, protectionThreshold, gamma) {
                // Misma conversión de Semitonos Fáciles:
                // color de prenda/fondo = 0 % tinta; color suficientemente distinto = 100 %.
                const safeGamma = Math.max(0.1, Number(gamma) || 1);
                const lowerBound = Math.max(0, Number(bgTolerance) || 0);
                const upperBound = Math.max(
                    lowerBound + 1,
                    Math.max(0, Number(protectionThreshold) || 0)
                );

                if (metric <= lowerBound) return 0;
                if (metric >= upperBound) return 1;

                const normalizedInk = clamp01((metric - lowerBound) / (upperBound - lowerBound));
                return applyGammaCorrection(normalizedInk, safeGamma);
            }

            // v2.2: "Tramado profesional sin perder color" fue eliminado. Este stub se
            // conserva SOLO porque applyFullColorCoverageBounds()/getRetainedDotSize()
            // (usadas por el semitono normal) siguen recibiendo este objeto como
            // parámetro; con enabled:false ambas funciones toman su rama de paso
            // directo (comportamiento idéntico al de siempre en modo normal).
            function getFullColorHalftoneOptions() {
                return {
                    enabled: false,
                    minCoverage: 0,
                    maxCoverage: 1,
                    preservationStrength: 0,
                    edgeSupportThreshold: 0,
                    edgeSoftness: 1
                };
            }

            function applyFullColorCoverageBounds(coverage, areaScale = 1, options = null) {
                const safeCoverage = clamp01(Number(coverage) || 0);
                const safeAreaScale = Math.max(0, Number(areaScale) || 0);
                const scaledCoverage = clamp01(safeCoverage * safeAreaScale);

                if (!options?.enabled) return scaledCoverage;
                if (scaledCoverage <= 0) return 0;

                const minCoverage = clamp01(options.minCoverage || 0);
                const maxCoverage = clamp01(options.maxCoverage || 1);

                if (scaledCoverage >= minCoverage) {
                    return Math.min(maxCoverage, scaledCoverage);
                }

                const ratio = clamp01(scaledCoverage / Math.max(minCoverage, 1e-6));
                // Elevación más conservadora: mantiene color sin inflar puntos en zonas casi vacías.
                const liftedCoverage = minCoverage * Math.pow(ratio, 0.82);
                return Math.min(maxCoverage, liftedCoverage);
            }

            function getRetainedDotSize(coverage, separation, maximumSize, dotShape, fullColorOptions = null) {
                const safeMaximum = Math.max(0.05, Number(maximumSize) || 1);

                // El control de tamaño trabaja sobre el área. En modo normal se conserva
                // el comportamiento anterior. Con "Tramado completo" el resultado final
                // queda limitado entre un piso y un techo de cobertura, de modo que ningún
                // color del diseño desaparezca y ninguna zona quede totalmente sólida.
                const adjustedCoverage = applyFullColorCoverageBounds(
                    coverage,
                    safeMaximum * safeMaximum,
                    fullColorOptions
                );

                if (dotShape === 'square') {
                    return separation * Math.sqrt(adjustedCoverage);
                }

                // Círculo y diamante alcanzan la diagonal de la celda al 100 %.
                return separation * Math.sqrt(2 * adjustedCoverage);
            }

            // Matriz de tramado agrupado 4 × 4. A diferencia del ruido aleatorio,
            // activa las celdas en grupos compactos y repetibles, evitando puntos sueltos
            // sin estructura en las zonas más suaves del degradado.
            const clusteredDotThresholds = new Uint8Array([
                12, 5, 6, 13,
                 4, 0, 1,  7,
                11, 3, 2,  8,
                15,10, 9, 14
            ]);

            // Los sellos se almacenan por diámetro entero. Cada círculo se rasteriza una
            // sola vez como píxeles binarios y después se reutiliza en toda la imagen.
            // Esto evita círculos deformes, rayas y figuras distintas causadas por el
            // anti-aliasing de arcos muy pequeños colocados sobre coordenadas fraccionarias.
            const crispCircleStampCache = new Map();

            function positiveModulo(value, divisor) {
                return ((value % divisor) + divisor) % divisor;
            }

            function getClusteredCellThreshold(gridX, gridY) {
                const x = positiveModulo(gridX, 4);
                const y = positiveModulo(gridY, 4);
                return (clusteredDotThresholds[y * 4 + x] + 0.5) / 16;
            }

            function normalizePrintableDotSize(dotSize, minimumSize, gridX, gridY) {
                if (dotSize >= minimumSize) return dotSize;
                if (dotSize <= 0) return 0;

                // Los tonos que todavía requieren menos área que el punto mínimo no se
                // representan con trazos subpíxel. Se distribuyen mediante una matriz
                // agrupada y cada punto conservado mantiene exactamente el mismo diámetro.
                const retainedArea = clamp01((dotSize * dotSize) / (minimumSize * minimumSize));
                return retainedArea >= getClusteredCellThreshold(gridX, gridY)
                    ? minimumSize
                    : 0;
            }

            function getCrispCircleStamp(requestedDiameter) {
                const diameter = Math.max(4, Math.round(Number(requestedDiameter) || 0));
                const cachedStamp = crispCircleStampCache.get(diameter);
                if (cachedStamp) return cachedStamp;

                const stamp = document.createElement('canvas');
                stamp.width = diameter;
                stamp.height = diameter;

                const stampCtx = stamp.getContext('2d');
                const imageData = stampCtx.createImageData(diameter, diameter);
                const pixels = imageData.data;
                const center = diameter / 2;
                const radius = Math.max(0.5, diameter / 2 - 0.08);
                const radiusSquared = radius * radius;

                for (let y = 0; y < diameter; y++) {
                    const dy = y + 0.5 - center;
                    for (let x = 0; x < diameter; x++) {
                        const dx = x + 0.5 - center;
                        if (dx * dx + dy * dy > radiusSquared) continue;

                        const index = (y * diameter + x) * 4;
                        pixels[index] = 255;
                        pixels[index + 1] = 255;
                        pixels[index + 2] = 255;
                        pixels[index + 3] = 255;
                    }
                }

                stampCtx.putImageData(imageData, 0, 0);
                crispCircleStampCache.set(diameter, stamp);
                return stamp;
            }

            function drawDefinedHalftoneShape(ctx, shape, cx, cy, size, angleRad) {
                if (shape !== 'circle') {
                    drawRotatedShape(ctx, shape, cx, cy, size, angleRad);
                    return;
                }

                const stamp = getCrispCircleStamp(size);
                const left = Math.round(cx - stamp.width / 2);
                const top = Math.round(cy - stamp.height / 2);
                ctx.drawImage(stamp, left, top);
            }


            // ═══════════════════════════════════════════════════════════════════
            // CAMPO CONTINUO PARA TRAMA CIRCULAR
            // En lugar de expandir sellos independientes, cada píxel se evalúa contra
            // una función periódica continua. Los puntos nacen redondos, crecen de forma
            // uniforme y, al llegar a tonos medios, se funden mediante curvas suaves.
            // ═══════════════════════════════════════════════════════════════════

            let roundWaveCoverageLut = null;
                        function getRoundWaveCoverageLut() {
                if (roundWaveCoverageLut) return roundWaveCoverageLut;

                // La tabla convierte cobertura de tinta (0–1) en el umbral del campo
                // sin²(πx) + sin²(πy). Se calcula una sola vez y conserva el tono real.
                const sampleSide = 256;
                const sampleCount = sampleSide * sampleSide;
                const samples = new Float32Array(sampleCount);
                let sampleIndex = 0;

                for (let y = 0; y < sampleSide; y++) {
                    const waveY = Math.sin(Math.PI * ((y + 0.5) / sampleSide));
                    const waveYSquared = waveY * waveY;

                    for (let x = 0; x < sampleSide; x++) {
                        const waveX = Math.sin(Math.PI * ((x + 0.5) / sampleSide));
                        samples[sampleIndex++] = waveX * waveX + waveYSquared;
                    }
                }

                samples.sort();

                const resolution = 2048;
                const lut = new Float32Array(resolution + 1);
                lut[0] = -0.000001;

                for (let i = 1; i < resolution; i++) {
                    const position = (i / resolution) * (sampleCount - 1);
                    const lowIndex = Math.floor(position);
                    const highIndex = Math.min(sampleCount - 1, lowIndex + 1);
                    const fraction = position - lowIndex;
                    lut[i] = samples[lowIndex] * (1 - fraction) + samples[highIndex] * fraction;
                }

                lut[resolution] = 2.000001;
                roundWaveCoverageLut = lut;
                return lut;
            }

            function coverageToRoundWaveThreshold(coverage) {
                const safeCoverage = clamp01(coverage);
                if (safeCoverage <= 0) return -0.000001;
                if (safeCoverage >= 1) return 2.000001;

                const lut = getRoundWaveCoverageLut();
                const position = safeCoverage * (lut.length - 1);
                const lowIndex = Math.floor(position);
                const highIndex = Math.min(lut.length - 1, lowIndex + 1);
                const fraction = position - lowIndex;
                return lut[lowIndex] * (1 - fraction) + lut[highIndex] * fraction;
            }

            function getRotatedGridBounds(width, height, offsetX, offsetY, cosA, sinA, separation) {
                const right = Math.max(0.5, width - 0.5);
                const bottom = Math.max(0.5, height - 0.5);
                const corners = [
                    [0.5, 0.5],
                    [right, 0.5],
                    [0.5, bottom],
                    [right, bottom]
                ];

                let minGridX = Infinity;
                let maxGridX = -Infinity;
                let minGridY = Infinity;
                let maxGridY = -Infinity;

                corners.forEach(([x, y]) => {
                    const dx = x - offsetX;
                    const dy = y - offsetY;
                    const gridX = (dx * cosA + dy * sinA) / separation;
                    const gridY = (-dx * sinA + dy * cosA) / separation;
                    minGridX = Math.min(minGridX, gridX);
                    maxGridX = Math.max(maxGridX, gridX);
                    minGridY = Math.min(minGridY, gridY);
                    maxGridY = Math.max(maxGridY, gridY);
                });

                return {
                    minGridX: Math.floor(minGridX) - 2,
                    maxGridX: Math.ceil(maxGridX) + 2,
                    minGridY: Math.floor(minGridY) - 2,
                    maxGridY: Math.ceil(maxGridY) + 2
                };
            }

            function buildContinuousCoverageGrid(
                originalData,
                connectedBackgroundMask,
                width,
                height,
                garmentMode,
                tR,
                tG,
                tB,
                bgTolerance,
                protectionThreshold,
                gamma,
                maximumSize,
                fullColorOptions,
                separation,
                offsetX,
                offsetY,
                cosA,
                sinA,
                requestId = processRequestId
            ) {
                const bounds = getRotatedGridBounds(
                    width,
                    height,
                    offsetX,
                    offsetY,
                    cosA,
                    sinA,
                    separation
                );
                const columns = bounds.maxGridX - bounds.minGridX + 1;
                const rows = bounds.maxGridY - bounds.minGridY + 1;
                const values = new Float32Array(columns * rows);
                const maximumScale = Math.max(0.05, Number(maximumSize) || 1);
                const areaScale = maximumScale * maximumScale;

                for (let gridY = bounds.minGridY; gridY <= bounds.maxGridY; gridY++) {
                    if (isProcessingRequestStale(requestId)) return null;
                    const localY = gridY - bounds.minGridY;

                    for (let gridX = bounds.minGridX; gridX <= bounds.maxGridX; gridX++) {
                        if ((gridX & 31) === 0 && isProcessingRequestStale(requestId)) return null;
                        const localX = gridX - bounds.minGridX;
                        const centerX = offsetX + (gridX * separation * cosA - gridY * separation * sinA);
                        const centerY = offsetY + (gridX * separation * sinA + gridY * separation * cosA);

                        const toneMetric = sampleToneForHalftone(
                            originalData,
                            connectedBackgroundMask,
                            centerX,
                            centerY,
                            width,
                            height,
                            garmentMode,
                            tR,
                            tG,
                            tB,
                            separation
                        );

                        if (toneMetric === null) continue;

                        const inkCoverage = getContinuousInkCoverage(
                            toneMetric,
                            garmentMode,
                            bgTolerance,
                            protectionThreshold,
                            gamma
                        );
                        values[localY * columns + localX] = applyFullColorCoverageBounds(
                            inkCoverage,
                            areaScale,
                            fullColorOptions
                        );
                    }
                }

                return {
                    ...bounds,
                    columns,
                    rows,
                    values
                };
            }

            function getCoverageGridValue(grid, gridX, gridY) {
                if (gridX < grid.minGridX || gridX > grid.maxGridX ||
                    gridY < grid.minGridY || gridY > grid.maxGridY) {
                    return 0;
                }

                const localX = gridX - grid.minGridX;
                const localY = gridY - grid.minGridY;
                return grid.values[localY * grid.columns + localX];
            }

            function sampleCoverageGrid(grid, screenX, screenY) {
                const x0 = Math.floor(screenX);
                const y0 = Math.floor(screenY);
                const localX = x0 - grid.minGridX;
                const localY = y0 - grid.minGridY;

                // Los límites incluyen dos celdas de margen, por lo que la ruta normal
                // accede directamente al arreglo sin cuatro validaciones por cada píxel.
                if (localX < 0 || localY < 0 ||
                    localX + 1 >= grid.columns || localY + 1 >= grid.rows) {
                    return 0;
                }

                const fractionX = screenX - x0;
                const fractionY = screenY - y0;
                const topIndex = localY * grid.columns + localX;
                const bottomIndex = topIndex + grid.columns;
                const c00 = grid.values[topIndex];
                const c10 = grid.values[topIndex + 1];
                const c01 = grid.values[bottomIndex];
                const c11 = grid.values[bottomIndex + 1];
                const top = c00 + (c10 - c00) * fractionX;
                const bottom = c01 + (c11 - c01) * fractionX;
                return top + (bottom - top) * fractionY;
            }
            function normalizeRoundCoverageForPrint(
                localCoverage,
                nearestCoverage,
                minimumCoverage,
                cellX,
                cellY
            ) {
                // Fórmula de Semitonos Fáciles: la cobertura interpolada conserva el
                // degradado continuo, mientras la cobertura de la celda más cercana decide
                // si un punto demasiado pequeño se conserva como mínimo imprimible o se elimina.
                if (nearestCoverage <= 0 || localCoverage <= 0) return 0;

                if (nearestCoverage < minimumCoverage) {
                    const retainedFraction = clamp01(nearestCoverage / minimumCoverage);
                    return retainedFraction >= getClusteredCellThreshold(cellX, cellY)
                        ? minimumCoverage
                        : 0;
                }

                return Math.max(localCoverage, minimumCoverage);
            }

            function stabilizeRoundWaveThreshold(threshold, separation) {
                // Alrededor del punto de silla del campo (valor 1), una transición exacta
                // puede producir un cuello o un canal de un solo píxel. Se salta únicamente
                // esa franja microscópica para que la unión o la separación sea imprimible.
                const minimumStableFeature = Math.max(1.25, Math.min(2.25, separation * 0.15));
                const saddleMargin = Math.min(
                    0.16,
                    Math.pow((Math.PI * minimumStableFeature) / (2 * separation), 2)
                );

                if (threshold > 1 - saddleMargin && threshold < 1) {
                    return 1 - saddleMargin;
                }
                if (threshold >= 1 && threshold < 1 + saddleMargin) {
                    return 1 + saddleMargin;
                }
                return threshold;
            }

            function removeSinglePixelMaskSpecks(mask, width, height) {
                if (width < 3 || height < 3) return mask;

                const cleaned = mask.slice();

                for (let y = 1; y < height - 1; y++) {
                    const rowOffset = y * width;

                    for (let x = 1; x < width - 1; x++) {
                        const index = rowOffset + x;
                        if (!mask[index]) continue;

                        let neighbors = 0;
                        for (let dy = -1; dy <= 1; dy++) {
                            const neighborRow = (y + dy) * width;
                            for (let dx = -1; dx <= 1; dx++) {
                                if (dx === 0 && dy === 0) continue;
                                neighbors += mask[neighborRow + x + dx];
                            }
                        }

                        if (neighbors <= 1) cleaned[index] = 0;
                    }
                }

                return cleaned;
            }

            function createContinuousRoundWaveMask(
                originalData,
                outputData,
                connectedBackgroundMask,
                width,
                height,
                garmentMode,
                tR,
                tG,
                tB,
                bgTolerance,
                protectionThreshold,
                gamma,
                maximumSize,
                fullColorOptions,
                separation,
                angleRad,
                useHardEdges,
                inkMaskCtx,
                requestId = processRequestId
            ) {
                // v2.13 — fórmula de Semitonos Fáciles + tamaño GEOMÉTRICO real.
                // Se abandona la geometría punto/hueco introducida en v2.9/v2.10 y se vuelve
                // al campo periódico continuo sin²(πx)+sin²(πy), que es el comportamiento
                // que ya funciona de forma estable en Semitonos Fáciles.
                const cosA = Math.cos(angleRad);
                const sinA = Math.sin(angleRad);
                const offsetX = width / 2;
                const offsetY = height / 2;

                // IMPORTANTE v2.13:
                // maximumSize NO puede modificar coverage ni threshold, porque hacerlo
                // equivale a remapear las luces/sombras de la imagen. El tono se calcula
                // exactamente como en Semitonos Fáciles y queda completamente intacto.
                //
                // maximumSize se usa únicamente como ESCALA ESPACIAL alrededor del centro
                // de cada celda. 0.5 contrae la geometría; 1.0 reproduce exactamente
                // Semitonos Fáciles; 1.5 expande la geometría. Los centros, la cantidad de
                // celdas y la cobertura tonal que decidió el motor NO cambian.
                const dotSizeScale = Math.max(0.05, Number(maximumSize) || 1);
                const useBaseGeometry = Math.abs(dotSizeScale - 1) < 1e-6;
                const coverageGrid = buildContinuousCoverageGrid(
                    originalData,
                    connectedBackgroundMask,
                    width,
                    height,
                    garmentMode,
                    tR,
                    tG,
                    tB,
                    bgTolerance,
                    protectionThreshold,
                    gamma,
                    1,
                    fullColorOptions,
                    separation,
                    offsetX,
                    offsetY,
                    cosA,
                    sinA,
                    requestId
                );
                if (!coverageGrid || isProcessingRequestStale(requestId)) return false;

                // Mismo mínimo de punto que usa Semitonos Fáciles. Es importante porque
                // evita residuos de 1–2 px y mantiene estable el punto de silla del campo.
                const requestedMinimumDot = useHardEdges
                    ? Math.max(4, Math.min(6, separation * 0.30))
                    : Math.max(3, Math.min(5, separation * 0.24));

                const minimumPrintableDot = Math.min(
                    requestedMinimumDot,
                    Math.max(3, separation * 0.78)
                );

                const minimumCoverage = clamp01(
                    (Math.PI * Math.pow(minimumPrintableDot / 2, 2)) /
                    (separation * separation)
                );

                let binaryMask = new Uint8Array(width * height);
                const screenStepX = cosA / separation;
                const screenStepY = -sinA / separation;
                const phaseStepX = Math.PI * screenStepX;
                const phaseStepY = Math.PI * screenStepY;
                const phaseStepCosX = Math.cos(phaseStepX);
                const phaseStepSinX = Math.sin(phaseStepX);
                const phaseStepCosY = Math.cos(phaseStepY);
                const phaseStepSinY = Math.sin(phaseStepY);

                for (let y = 0; y < height; y++) {
                    if ((y & 7) === 0 && isProcessingRequestStale(requestId)) return false;

                    const pixelY = y + 0.5;
                    const startDx = 0.5 - offsetX;
                    const startDy = pixelY - offsetY;
                    let screenX = (startDx * cosA + startDy * sinA) / separation;
                    let screenY = (-startDx * sinA + startDy * cosA) / separation;
                    let waveSinX = Math.sin(Math.PI * screenX);
                    let waveCosX = Math.cos(Math.PI * screenX);
                    let waveSinY = Math.sin(Math.PI * screenY);
                    let waveCosY = Math.cos(Math.PI * screenY);
                    const rowOffset = y * width;

                    for (let x = 0; x < width; x++) {
                        if ((x & 255) === 0 && isProcessingRequestStale(requestId)) return false;

                        const pixelIndex = rowOffset + x;
                        const dataIndex = pixelIndex * 4;

                        // Igual que Semitonos Fáciles: el fondo se corta ANTES de evaluar
                        // el campo. No se crean círculos completos para recortarlos después.
                        if (!connectedBackgroundMask[pixelIndex] && outputData[dataIndex + 3] > 8) {
                            const localCoverage = sampleCoverageGrid(coverageGrid, screenX, screenY);
                            const cellX = Math.round(screenX);
                            const cellY = Math.round(screenY);
                            const nearestCoverage = getCoverageGridValue(coverageGrid, cellX, cellY);
                            const printableCoverage = normalizeRoundCoverageForPrint(
                                localCoverage,
                                nearestCoverage,
                                minimumCoverage,
                                cellX,
                                cellY
                            );

                            if (printableCoverage > 0) {
                                // El threshold depende EXCLUSIVAMENTE del tono original.
                                // maxSize no participa aquí: así no modifica luces, gamma,
                                // protección ni la cantidad de celdas activas.
                                let threshold = coverageToRoundWaveThreshold(printableCoverage);
                                threshold = stabilizeRoundWaveThreshold(threshold, separation);

                                let waveField;
                                if (useBaseGeometry) {
                                    // 1.0 = fórmula EXACTA de Semitonos Fáciles.
                                    waveField = waveSinX * waveSinX + waveSinY * waveSinY;
                                } else {
                                    // v2.14: la escala espacial basada en sin²(x)+sin²(y)
                                    // agranda/reduce bien el punto, pero deforma los contornos
                                    // hacia rombos/cuadrados porque sus isolíneas son separables.
                                    //
                                    // Para que "Tamaño Máximo del Punto" mantenga apariencia
                                    // circular, cuando el usuario se sale de 1.0 conservamos el
                                    // MISMO threshold tonal, pero evaluamos un campo radial en la
                                    // celda actual. El rango se normaliza a 0..2 igual que la
                                    // fórmula de Semitonos Fáciles:
                                    //   centro = 0
                                    //   mitad del lado = 1
                                    //   esquina = 2
                                    // Así el tono no cambia y el tamaño solo modifica el radio.
                                    const localX = (screenX - cellX) / dotSizeScale;
                                    const localY = (screenY - cellY) / dotSizeScale;
                                    waveField = 4 * (localX * localX + localY * localY);
                                }

                                if (waveField <= threshold) {
                                    binaryMask[pixelIndex] = 1;
                                }
                            }
                        }

                        screenX += screenStepX;
                        screenY += screenStepY;

                        const nextWaveSinX = waveSinX * phaseStepCosX + waveCosX * phaseStepSinX;
                        waveCosX = waveCosX * phaseStepCosX - waveSinX * phaseStepSinX;
                        waveSinX = nextWaveSinX;

                        const nextWaveSinY = waveSinY * phaseStepCosY + waveCosY * phaseStepSinY;
                        waveCosY = waveCosY * phaseStepCosY - waveSinY * phaseStepSinY;
                        waveSinY = nextWaveSinY;

                        // Igual que Semitonos Fáciles: recalibración periódica para evitar
                        // acumulación numérica en filas anchas.
                        if ((x & 255) === 255) {
                            waveSinX = Math.sin(Math.PI * screenX);
                            waveCosX = Math.cos(Math.PI * screenX);
                            waveSinY = Math.sin(Math.PI * screenY);
                            waveCosY = Math.cos(Math.PI * screenY);
                        }
                    }
                }

                // Misma limpieza de Semitonos Fáciles: solo elimina residuos realmente
                // aislados, sin deformar el patrón general.
                binaryMask = removeSinglePixelMaskSpecks(binaryMask, width, height);
                if (isProcessingRequestStale(requestId)) return false;

                const maskImageData = inkMaskCtx.createImageData(width, height);
                const maskPixels = maskImageData.data;

                for (let pixelIndex = 0; pixelIndex < binaryMask.length; pixelIndex++) {
                    if ((pixelIndex & 8191) === 0 && isProcessingRequestStale(requestId)) return false;
                    if (!binaryMask[pixelIndex]) continue;
                    const dataIndex = pixelIndex * 4;
                    maskPixels[dataIndex] = 255;
                    maskPixels[dataIndex + 1] = 255;
                    maskPixels[dataIndex + 2] = 255;
                    maskPixels[dataIndex + 3] = 255;
                }

                inkMaskCtx.putImageData(maskImageData, 0, 0);
                return true;
            }

            // ═══════════════════════════════════════════════════════════════════
            // UTILIDADES DE MODO PERSONALIZADO: Distancia de color (SIN MODIFICAR)
            // ═══════════════════════════════════════════════════════════════════

            // Muestreo 3x3 usando distancia de color (equivalente a sample3x3 para modo custom)
            function sampleColorDistNorm3x3(data, centerX, centerY, width, height, tR, tG, tB) {
                let totalDist = 0;
                let count = 0;
                for (let dy = -1; dy <= 1; dy++) {
                    for (let dx = -1; dx <= 1; dx++) {
                        const sx = Math.round(centerX + dx);
                        const sy = Math.round(centerY + dy);
                        if (sx >= 0 && sx < width && sy >= 0 && sy < height) {
                            const index = (sy * width + sx) * 4;
                            totalDist += getColorDistNorm(data[index], data[index + 1], data[index + 2], tR, tG, tB);
                            count++;
                        }
                    }
                }
                return count > 0 ? totalDist / count : 255;
            }

            // ═══════════════════════════════════════════════════════════════════
            // FUNCIÓN PRINCIPAL: processImage
            // SEPARACIÓN LÓGICA TOTAL ENTRE MODO DARK Y LIGHT (SIN MODIFICAR)
            // ═══════════════════════════════════════════════════════════════════

            // Segunda etapa sustractiva: nunca crea tinta ni recupera el fondo.
            // Perforaciones en los huecos de la misma retícula (mismo ángulo,
            // periodo y origen), sin otra frecuencia que produzca interferencias.
            function applyCompleteHalftone(data, width, height, separation, angleRad, requestId) {
                const cosA = Math.cos(angleRad);
                const sinA = Math.sin(angleRad);
                const stepX = cosA / separation;
                const stepY = -sinA / separation;
                // Aproximadamente 12 % de área libre en zonas completamente sólidas.
                const radiusSquared = 0.12 / Math.PI;
                for (let y = 0; y < height; y++) {
                    if ((y & 7) === 0 && isProcessingRequestStale(requestId)) return false;
                    const dy = y + 0.5 - height / 2;
                    const dx = 0.5 - width / 2;
                    let sx = (dx * cosA + dy * sinA) / separation;
                    let sy = (-dx * sinA + dy * cosA) / separation;
                    for (let x = 0; x < width; x++, sx += stepX, sy += stepY) {
                        const alphaIndex = (y * width + x) * 4 + 3;
                        if (data[alphaIndex] === 0) continue;
                        const holeX = sx - Math.floor(sx) - 0.5;
                        const holeY = sy - Math.floor(sy) - 0.5;
                        if (holeX * holeX + holeY * holeY <= radiusSquared) {
                            data[alphaIndex] = 0;
                        }
                    }
                }
                return true;
            }

            function performImageProcessing(requestId) {
                if (!currentImage || requestId !== processRequestId) return;

                const targetSize = getCurrentTargetPixelSize();
                const width = targetSize.width;
                const height = targetSize.height;
                const displayWidth = width;
                const displayHeight = height;

                const bgTolerance = parseInt(bgRemovalInput.value, 10);
                const protectionThreshold = parseInt(thresholdInput.value, 10);
                // v2.3: Semitonos Fáciles siempre exige un mínimo de 4 px de separación
                // (ver `Math.max(4, screenSizeMm * mmToPx)` en su motor). Sin ese piso,
                // el campo senoidal que dibuja los puntos redondos ya no tiene pixeles
                // suficientes por periodo para resolver un círculo: en vez de un punto
                // se ve ruido/cuadrícula (aliasing), que es justo la trama "cuadrada"
                // reportada. Aplicamos el mismo piso aquí, en la ÚNICA fuente de la que
                // beben tanto el modo círculo (createContinuousRoundWaveMask) como el
                // modo cuadrado/diamante, sin tocar ningún otro comportamiento.
                // `separacion` controla la frecuencia espacial de la trama, igual que
                // screenSize en Semitonos Fáciles: valor mayor = periodo mayor = MENOS
                // centros/puntos y más separación entre ellos. No se invierte.
                const separacion = Math.max(4, parseFloat(separationInput.value) || 4);
                // `tamanoMaximo` NO controla densidad; solo diámetro dentro de cada celda.
                const tamanoMaximo = Math.max(0.05, parseFloat(maxSizeInput.value) || 1);
                const screenAngle = parseInt(screenAngleInput.value, 10);
                const gamma = parseFloat(gammaInput.value);
                const dotShape = dotShapeSelect.value;
                const useHardEdges = hardEdgesCheckbox.checked;
                const garmentMode = garmentModeSelect.value;
                const fullColorOptions = getFullColorHalftoneOptions();

                let tR = 0, tG = 0, tB = 0;
                if (garmentMode === 'custom') {
                    const hexG = customGarmentColorInput.value;
                    tR = parseInt(hexG.slice(1, 3), 16);
                    tG = parseInt(hexG.slice(3, 5), 16);
                    tB = parseInt(hexG.slice(5, 7), 16);
                }

                const angleRad = (screenAngle * Math.PI) / 180;
                const cosA = Math.cos(angleRad);
                const sinA = Math.sin(angleRad);

                const preparedSource = prepareSourceForProcessing(
                    width,
                    height,
                    garmentMode,
                    bgTolerance,
                    tR,
                    tG,
                    tB
                );
                const originalData = preparedSource.originalData;
                const connectedBackgroundMask = preparedSource.connectedBackgroundMask;
                const outputData = preparedSource.outputData;

                if (renderCanvas.width !== width || renderCanvas.height !== height) {
                    renderCanvas.width = width;
                    renderCanvas.height = height;
                }
                renderCtx.clearRect(0, 0, width, height);
                renderCtx.imageSmoothingEnabled = false;

                const activeProtectedColors = getActiveProtectedColors();
                const protectedTolerance = parseInt(protectedColorToleranceInput.value, 10);
                let hasProtectedPixels = false;

                if (activeProtectedColors.length > 0) {
                    const protectedImageData = protectedRestoreCtx.createImageData(width, height);
                    const protectedData = protectedImageData.data;

                    for (let i = 0, pixelIndex = 0; i < originalData.length; i += 4, pixelIndex++) {
                        if ((pixelIndex & 8191) === 0 && isProcessingRequestStale(requestId)) return;
                        if (connectedBackgroundMask[pixelIndex] || outputData[i + 3] === 0) continue;

                        const r = originalData[i];
                        const g = originalData[i + 1];
                        const b = originalData[i + 2];
                        const matchesProtectedColor = activeProtectedColors.some((color) =>
                            getColorDistNorm(r, g, b, color.r, color.g, color.b) <= protectedTolerance
                        );

                        if (!matchesProtectedColor) continue;

                        protectedData[i] = outputData[i];
                        protectedData[i + 1] = outputData[i + 1];
                        protectedData[i + 2] = outputData[i + 2];
                        protectedData[i + 3] = outputData[i + 3];
                        hasProtectedPixels = true;
                    }

                    protectedRestoreCtx.clearRect(0, 0, width, height);
                    protectedRestoreCtx.putImageData(protectedImageData, 0, 0);
                } else {
                    protectedRestoreCtx.clearRect(0, 0, width, height);
                }

                {
                    if (inkMaskCanvas.width !== width || inkMaskCanvas.height !== height) {
                        inkMaskCanvas.width = width;
                        inkMaskCanvas.height = height;
                    }
                    inkMaskCtx.clearRect(0, 0, width, height);
                    inkMaskCtx.fillStyle = 'rgba(255, 255, 255, 1)';
                    inkMaskCtx.imageSmoothingEnabled = false;

                    if (dotShape === 'circle') {
                        const maskBuilt = createContinuousRoundWaveMask(
                            originalData,
                            outputData,
                            connectedBackgroundMask,
                            width,
                            height,
                            garmentMode,
                            tR,
                            tG,
                            tB,
                            bgTolerance,
                            protectionThreshold,
                            gamma,
                            tamanoMaximo,
                            fullColorOptions,
                            separacion,
                            angleRad,
                            useHardEdges,
                            inkMaskCtx,
                            requestId
                        );
                        if (!maskBuilt || isProcessingRequestStale(requestId)) return;
                    } else {
                        const diagonal = Math.sqrt(width * width + height * height);
                        const gridExtent = Math.ceil(diagonal / separacion) + 2;
                        const offsetX = width / 2;
                        const offsetY = height / 2;
                        const requestedMinimumDot = useHardEdges
                            ? Math.max(4, Math.min(6, separacion * 0.30))
                            : Math.max(3, Math.min(5, separacion * 0.24));
                        const minimumPrintableDot = Math.min(
                            requestedMinimumDot,
                            Math.max(3, separacion * 0.78)
                        );

                        for (let gy = -gridExtent; gy <= gridExtent; gy++) {
                            if (isProcessingRequestStale(requestId)) return;
                            for (let gx = -gridExtent; gx <= gridExtent; gx++) {
                                if ((gx & 31) === 0 && isProcessingRequestStale(requestId)) return;
                                const gridX = gx * separacion;
                                const gridY = gy * separacion;
                                const rotatedX = gridX * cosA - gridY * sinA;
                                const rotatedY = gridX * sinA + gridY * cosA;
                                const centerX = rotatedX + offsetX;
                                const centerY = rotatedY + offsetY;

                                if (centerX < -separacion || centerX > width + separacion ||
                                    centerY < -separacion || centerY > height + separacion) {
                                    continue;
                                }

                                const centerPixelX = Math.round(centerX);
                                const centerPixelY = Math.round(centerY);
                                if (centerPixelX < 0 || centerPixelX >= width ||
                                    centerPixelY < 0 || centerPixelY >= height) {
                                    continue;
                                }

                                const centerPixelIndex = centerPixelY * width + centerPixelX;
                                if (connectedBackgroundMask[centerPixelIndex] ||
                                    outputData[centerPixelIndex * 4 + 3] <= 8) {
                                    continue;
                                }

                                const toneMetric = sampleToneForHalftone(
                                    originalData,
                                    connectedBackgroundMask,
                                    centerX,
                                    centerY,
                                    width,
                                    height,
                                    garmentMode,
                                    tR,
                                    tG,
                                    tB,
                                    separacion
                                );
                                if (toneMetric === null) continue;

                                const inkCoverage = getContinuousInkCoverage(
                                    toneMetric,
                                    garmentMode,
                                    bgTolerance,
                                    protectionThreshold,
                                    gamma
                                );

                                let dotSize = getRetainedDotSize(
                                    inkCoverage,
                                    separacion,
                                    tamanoMaximo,
                                    dotShape,
                                    fullColorOptions
                                );

                                dotSize = normalizePrintableDotSize(
                                    dotSize,
                                    minimumPrintableDot,
                                    gx,
                                    gy
                                );
                                if (dotSize <= 0) continue;

                                dotSize = Math.round(dotSize * 32) / 32;
                                drawDefinedHalftoneShape(
                                    inkMaskCtx,
                                    dotShape,
                                    centerX,
                                    centerY,
                                    dotSize,
                                    angleRad
                                );
                            }
                        }

                        const maskData = inkMaskCtx.getImageData(0, 0, width, height);
                        const maskPixels = maskData.data;
                        for (let i = 3, pixelIndex = 0; i < maskPixels.length; i += 4, pixelIndex++) {
                            if ((pixelIndex & 8191) === 0 && isProcessingRequestStale(requestId)) return;
                            maskPixels[i] = maskPixels[i] >= 128 ? 255 : 0;
                        }
                        inkMaskCtx.putImageData(maskData, 0, 0);
                    }

                    renderCtx.globalCompositeOperation = 'source-over';
                    renderCtx.drawImage(baseProcessedCanvas, 0, 0);
                    renderCtx.globalCompositeOperation = 'destination-in';
                    renderCtx.drawImage(inkMaskCanvas, 0, 0);
                    renderCtx.globalCompositeOperation = 'source-over';
                }

                // El color principal ya fue eliminado y la trama normal ya se aplicó.
                if (fullHalftoneCheckbox.checked) {
                    const completeData = renderCtx.getImageData(0, 0, width, height);
                    if (!applyCompleteHalftone(completeData.data, width, height, separacion, angleRad, requestId)) return;
                    renderCtx.putImageData(completeData, 0, 0);
                }

                if (hasProtectedPixels) {
                    renderCtx.drawImage(protectedRestoreCanvas, 0, 0);
                }

                restoreThroughManualMask(renderCtx);

                if (useHardEdges) {
                    applyHardEdges(renderCtx, width, height);
                }

                if (requestId !== processRequestId) return;

                // Commit atómico: el canvas visible conserva el resultado anterior durante
                // todo el cálculo y se reemplaza solo cuando el nuevo semitono está completo.
                if (outputCanvas.width !== displayWidth || outputCanvas.height !== displayHeight) {
                    outputCanvas.width = displayWidth;
                    outputCanvas.height = displayHeight;
                }
                outputCtx.globalCompositeOperation = 'copy';
                outputCtx.imageSmoothingEnabled = false;
                outputCtx.imageSmoothingQuality = 'high';
                outputCtx.drawImage(renderCanvas, 0, 0, displayWidth, displayHeight);
                outputCtx.globalCompositeOperation = 'source-over';

                if (width * height > MAX_PERSISTENT_CACHE_PIXELS) {
                    preparedSourceCache.originalImageData = null;
                }
            }

            function processImage(delay = 60) {
                if (!currentImage) return;

                const requestId = ++processRequestId;
                if (processTimer) {
                    clearTimeout(processTimer);
                    processTimer = null;
                }
                if (processFrame) {
                    cancelAnimationFrame(processFrame);
                    processFrame = null;
                }
                appRoot.classList.remove('is-processing');
                setProcessingOverlay(false);

                processTimer = setTimeout(() => {
                    processTimer = null;
                    appRoot.classList.add('is-processing');
                    setProcessingOverlay(true, 'Procesando cambios…');

                    const handleProcessingError = (error) => {
                        console.error('Error al procesar el semitono:', error);
                        processingMessage.style.color = '#ff4757';
                        processingMessage.textContent = 'No se pudo procesar la imagen. Revisa la medida e intenta nuevamente.';
                        setTimeout(() => {
                            processingMessage.textContent = '';
                            processingMessage.style.color = '#E3FC02';
                        }, 4000);
                    };

                    processFrame = requestAnimationFrame(() => {
                        processFrame = null;
                        try {
                            if (isProcessingRequestStale(requestId)) return;
                            performImageProcessing(requestId);
                        } catch (error) {
                            handleProcessingError(error);
                        } finally {
                            if (requestId === processRequestId) {
                                appRoot.classList.remove('is-processing');
                                setProcessingOverlay(false);
                            }
                        }
                    });
                }, Math.max(0, Number(delay) || 0));
            }

            // ═══════════════════════════════════════════════════════════════════
            // MEDIDAS DE DESCARGA + EXPORTACIÓN PNG
            // ═══════════════════════════════════════════════════════════════════

            function getSafeDpi() {
                const dpi = Math.round(parseFloat(exportDpiInput.value) || 300);
                return Math.max(72, Math.min(1200, dpi));
            }

            function formatMeasureValue(value, unit) {
                if (!Number.isFinite(value) || value <= 0) return '0';
                return unit === 'px' ? String(Math.max(1, Math.round(value))) : value.toFixed(2);
            }

            function pixelsToSelectedUnit(pixels, unit, dpi) {
                if (unit === 'cm') return (pixels / dpi) * 2.54;
                if (unit === 'in') return pixels / dpi;
                return pixels;
            }

            function selectedUnitToPixels(value, unit, dpi) {
                const safeValue = Math.max(1, parseFloat(value) || 1);
                if (unit === 'cm') return Math.max(1, Math.round((safeValue / 2.54) * dpi));
                if (unit === 'in') return Math.max(1, Math.round(safeValue * dpi));
                return Math.max(1, Math.round(safeValue));
            }

            function getCurrentTargetPixelSize() {
                const dpi = getSafeDpi();
                const unit = exportUnitSelect.value;
                const fallbackWidth = currentImage?.naturalWidth || currentImage?.width || outputCanvas.width || 1;
                const fallbackHeight = currentImage?.naturalHeight || currentImage?.height || outputCanvas.height || 1;

                const targetWidth = exportWidthInput.value
                    ? selectedUnitToPixels(exportWidthInput.value, unit, dpi)
                    : Math.max(1, Math.round(fallbackWidth));
                const targetHeight = exportHeightInput.value
                    ? selectedUnitToPixels(exportHeightInput.value, unit, dpi)
                    : Math.max(1, Math.round(fallbackHeight));

                return {
                    width: Math.max(1, targetWidth),
                    height: Math.max(1, targetHeight)
                };
            }

            function processImageFromCurrentMeasures(delay = 220) {
                updateExportSizeHelp();
                if (currentImage) processImage(delay);
            }

            function updateDownloadSizeFieldsFromSourceImage() {
                if (!currentImage) return;

                isSyncingExportSize = true;

                const dpi = getSafeDpi();
                const unit = exportUnitSelect.value;
                const sourceWidth = currentImage.naturalWidth || currentImage.width || 1;
                const sourceHeight = currentImage.naturalHeight || currentImage.height || 1;

                exportAspectRatio = sourceWidth / sourceHeight;
                exportWidthInput.step = unit === 'px' ? '1' : '0.01';
                exportHeightInput.step = unit === 'px' ? '1' : '0.01';

                exportWidthInput.value = formatMeasureValue(pixelsToSelectedUnit(sourceWidth, unit, dpi), unit);
                exportHeightInput.value = formatMeasureValue(pixelsToSelectedUnit(sourceHeight, unit, dpi), unit);

                isSyncingExportSize = false;
                updateExportSizeHelp();
            }

            function updateDownloadSizeFieldsPreservingPixels() {
                if (!outputCanvas.width || !outputCanvas.height) {
                    updateDownloadSizeFieldsFromSourceImage();
                    return;
                }

                isSyncingExportSize = true;

                const dpi = getSafeDpi();
                const unit = exportUnitSelect.value;

                exportAspectRatio = outputCanvas.width / outputCanvas.height;
                exportWidthInput.step = unit === 'px' ? '1' : '0.01';
                exportHeightInput.step = unit === 'px' ? '1' : '0.01';

                exportWidthInput.value = formatMeasureValue(pixelsToSelectedUnit(outputCanvas.width, unit, dpi), unit);
                exportHeightInput.value = formatMeasureValue(pixelsToSelectedUnit(outputCanvas.height, unit, dpi), unit);

                isSyncingExportSize = false;
                updateExportSizeHelp();
            }

            function updateExportAspectRatioFromCurrentMeasures() {
                const dpi = getSafeDpi();
                const unit = exportUnitSelect.value;
                const widthPx = selectedUnitToPixels(exportWidthInput.value, unit, dpi);
                const heightPx = selectedUnitToPixels(exportHeightInput.value, unit, dpi);
                exportAspectRatio = widthPx / heightPx;
            }

            function updateExportSizeHelp() {
                if (!outputCanvas.width || !outputCanvas.height) return;

                const dpi = getSafeDpi();
                const unit = exportUnitSelect.value;
                const targetWidth = selectedUnitToPixels(exportWidthInput.value, unit, dpi);
                const targetHeight = selectedUnitToPixels(exportHeightInput.value, unit, dpi);

                exportSizeHelp.textContent =
                    `Salida exacta: ${targetWidth} × ${targetHeight} px · ${dpi} DPI.`;
            }

            function updateDownloadSizeFieldsFromCanvas() {
                updateDownloadSizeFieldsPreservingPixels();
            }

            function syncExportMeasure(changedField) {
                if (isSyncingExportSize || !lockExportRatioCheckbox.checked || !exportAspectRatio) {
                    updateExportSizeHelp();
                    return;
                }

                isSyncingExportSize = true;

                const unit = exportUnitSelect.value;
                const widthValue = parseFloat(exportWidthInput.value) || 1;
                const heightValue = parseFloat(exportHeightInput.value) || 1;

                if (changedField === 'width') {
                    exportHeightInput.value = formatMeasureValue(widthValue / exportAspectRatio, unit);
                } else {
                    exportWidthInput.value = formatMeasureValue(heightValue * exportAspectRatio, unit);
                }

                isSyncingExportSize = false;
                updateExportSizeHelp();
            }

            function createDownloadCanvas(targetWidth, targetHeight) {
                const exportCanvas = document.createElement('canvas');
                exportCanvas.width = targetWidth;
                exportCanvas.height = targetHeight;

                const exportCtx = exportCanvas.getContext('2d');
                exportCtx.clearRect(0, 0, targetWidth, targetHeight);
                exportCtx.imageSmoothingEnabled = true;
                exportCtx.imageSmoothingQuality = 'high';
                exportCtx.drawImage(outputCanvas, 0, 0, targetWidth, targetHeight);

                // Si el usuario tiene activos bordes duros, mantener la descarga sin semitransparencias
                // incluso cuando se escala a una medida diferente.
                if (hardEdgesCheckbox.checked) {
                    applyHardEdges(exportCtx, targetWidth, targetHeight);
                }

                return exportCanvas;
            }

            function makeCrcTable() {
                const table = new Uint32Array(256);
                for (let n = 0; n < 256; n++) {
                    let c = n;
                    for (let k = 0; k < 8; k++) {
                        c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
                    }
                    table[n] = c >>> 0;
                }
                return table;
            }

            const pngCrcTable = makeCrcTable();

            function crc32(bytes) {
                let crc = 0xFFFFFFFF;
                for (let i = 0; i < bytes.length; i++) {
                    crc = pngCrcTable[(crc ^ bytes[i]) & 0xFF] ^ (crc >>> 8);
                }
                return (crc ^ 0xFFFFFFFF) >>> 0;
            }

            function writeUint32(bytes, offset, value) {
                bytes[offset] = (value >>> 24) & 0xFF;
                bytes[offset + 1] = (value >>> 16) & 0xFF;
                bytes[offset + 2] = (value >>> 8) & 0xFF;
                bytes[offset + 3] = value & 0xFF;
            }

            function injectPngDpiMetadata(arrayBuffer, dpi) {
                const originalBytes = new Uint8Array(arrayBuffer);
                const ppm = Math.round(dpi / 0.0254);

                // Chunk pHYs: 4 bytes X ppm + 4 bytes Y ppm + 1 byte unit specifier.
                const chunkLength = 9;
                const chunkType = new TextEncoder().encode('pHYs');
                const chunkData = new Uint8Array(chunkLength);
                writeUint32(chunkData, 0, ppm);
                writeUint32(chunkData, 4, ppm);
                chunkData[8] = 1;

                const crcInput = new Uint8Array(chunkType.length + chunkData.length);
                crcInput.set(chunkType, 0);
                crcInput.set(chunkData, chunkType.length);

                const chunk = new Uint8Array(4 + 4 + chunkLength + 4);
                writeUint32(chunk, 0, chunkLength);
                chunk.set(chunkType, 4);
                chunk.set(chunkData, 8);
                writeUint32(chunk, 8 + chunkLength, crc32(crcInput));

                // Insertar después del IHDR. Firma PNG (8) + IHDR total (25) = offset 33.
                const insertOffset = 33;
                const finalBytes = new Uint8Array(originalBytes.length + chunk.length);
                finalBytes.set(originalBytes.slice(0, insertOffset), 0);
                finalBytes.set(chunk, insertOffset);
                finalBytes.set(originalBytes.slice(insertOffset), insertOffset + chunk.length);

                return new Blob([finalBytes], { type: 'image/png' });
            }

            function canvasToPngBlob(canvas) {
                return new Promise((resolve) => {
                    canvas.toBlob((blob) => resolve(blob), 'image/png');
                });
            }

            async function downloadCanvasWithExactSize() {
                if (!currentImage || !outputCanvas.width || !outputCanvas.height) return;

                downloadBtn.disabled = true;
                const previousText = downloadBtn.textContent;
                downloadBtn.textContent = 'Preparando descarga...';

                try {
                    const dpi = getSafeDpi();
                    exportDpiInput.value = dpi;

                    const unit = exportUnitSelect.value;
                    const targetWidth = selectedUnitToPixels(exportWidthInput.value, unit, dpi);
                    const targetHeight = selectedUnitToPixels(exportHeightInput.value, unit, dpi);

                    const exportCanvas = createDownloadCanvas(targetWidth, targetHeight);
                    const rawBlob = await canvasToPngBlob(exportCanvas);
                    const rawBuffer = await rawBlob.arrayBuffer();
                    const finalBlob = injectPngDpiMetadata(rawBuffer, dpi);

                    const url = URL.createObjectURL(finalBlob);
                    const link = document.createElement('a');
                    link.download = `${originalFileName}_Semitono Novage_${targetWidth}x${targetHeight}px_${dpi}dpi.png`;
                    link.href = url;
                    link.click();

                    setTimeout(() => URL.revokeObjectURL(url), 1000);
                } finally {
                    downloadBtn.textContent = previousText;
                    downloadBtn.disabled = false;
                }
            }

            downloadBtn.addEventListener('click', downloadCanvasWithExactSize);

            // ═══════════════════════════════════════════════════════════════════
            // REINICIAR EDICIONES — restaura exactamente los valores iniciales
            // de esta página sin modificar la imagen cargada ni la vista.
            // ═══════════════════════════════════════════════════════════════════
            let defaultEditState = null;

            function captureDefaultEditState() {
                defaultEditState = {
                    garmentMode: garmentModeSelect.value,
                    customGarmentColor: customGarmentColorInput.value,
                    bgRemoval: bgRemovalInput.value,
                    threshold: thresholdInput.value,
                    separation: separationInput.value,
                    maxSize: maxSizeInput.value,
                    screenAngle: screenAngleInput.value,
                    gamma: gammaInput.value,
                    dotShape: dotShapeSelect.value,
                    hardEdges: hardEdgesCheckbox.checked,
                    fullHalftone: fullHalftoneCheckbox.checked,
                    protectColorsEnabled: protectColorsEnabled.checked,
                    protectedColorTolerance: protectedColorToleranceInput.value,
                    protectedColorsMarkup: protectedColorsGrid.innerHTML,
                    restoreBrushSize: restoreBrushSizeInput.value
                };
            }

            function refreshEditControlLabels() {
                const bgDisplay = document.getElementById('bgRemovalValue');
                const thresholdDisplay = document.getElementById('thresholdValue');
                if (bgDisplay) bgDisplay.textContent = bgRemovalInput.value;
                if (thresholdDisplay) thresholdDisplay.textContent = thresholdInput.value;
                if (separationValue) separationValue.textContent = separationInput.value;
                if (maxSizeValue) maxSizeValue.textContent = Number(maxSizeInput.value).toFixed(1);
                if (screenAngleValue) screenAngleValue.textContent = `${screenAngleInput.value}°`;
                if (gammaValue) gammaValue.textContent = Number(gammaInput.value).toFixed(1);
                if (protectedColorToleranceValue) protectedColorToleranceValue.textContent = protectedColorToleranceInput.value;
                if (restoreBrushSizeValue) restoreBrushSizeValue.textContent = `${restoreBrushSizeInput.value} px`;
            }

            function resetEditsToDefaults() {
                if (!defaultEditState) return;

                activeColorPickerIndex = -1;
                getProtectedColorPickerButtons().forEach((button) => button.classList.remove('active'));
                canvasContainer.classList.remove('color-pick-mode');

                isRestoreBrushActive = false;
                isBrushDrawing = false;
                lastBrushPoint = null;
                restoreBrushBtn.classList.remove('active');
                canvasContainer.classList.remove('brush-mode');
                brushCursor.style.display = 'none';

                garmentModeSelect.value = defaultEditState.garmentMode;
                customGarmentColorInput.value = defaultEditState.customGarmentColor;
                bgRemovalInput.value = defaultEditState.bgRemoval;
                thresholdInput.value = defaultEditState.threshold;
                separationInput.value = defaultEditState.separation;
                maxSizeInput.value = defaultEditState.maxSize;
                screenAngleInput.value = defaultEditState.screenAngle;
                gammaInput.value = defaultEditState.gamma;
                dotShapeSelect.value = defaultEditState.dotShape;
                hardEdgesCheckbox.checked = defaultEditState.hardEdges;
                fullHalftoneCheckbox.checked = defaultEditState.fullHalftone;

                protectColorsEnabled.checked = defaultEditState.protectColorsEnabled;
                protectedColorToleranceInput.value = defaultEditState.protectedColorTolerance;
                protectedColorsGrid.innerHTML = defaultEditState.protectedColorsMarkup;
                updateProtectedColorUI();

                restoreBrushSizeInput.value = defaultEditState.restoreBrushSize;
                manualRestoreMaskCtx.clearRect(0, 0, manualRestoreMaskCanvas.width, manualRestoreMaskCanvas.height);

                garmentColorBgBtn.style.backgroundColor = customGarmentColorInput.value;
                if (garmentColorBgBtn.classList.contains('active')) {
                    canvasContainer.classList.remove('checkerboard-bg', 'checkerboard-bg-white');
                    canvasContainer.style.backgroundImage = 'none';
                    canvasContainer.style.backgroundColor = customGarmentColorInput.value;
                }

                updateUIForMode();
                refreshEditControlLabels();
                if (currentImage) processImage(0);
            }

            function installResetEditsButton() {
                if (document.getElementById('resetEditsBtn')) return;

                const button = document.createElement('button');
                button.type = 'button';
                button.id = 'resetEditsBtn';
                button.setAttribute('aria-label', 'Reiniciar ediciones y ajustes');
                button.title = 'Restaurar los ajustes recomendados';
                button.innerHTML = '<span class="reset-edits-icon" aria-hidden="true">↺</span><span>Reiniciar Ediciones</span>';
                button.addEventListener('click', resetEditsToDefaults);

                const mainContent = appRoot.querySelector('.main-content');
                const toolbar = appRoot.querySelector('.preview-toolbar');
                if (mainContent) mainContent.appendChild(button);
                else if (toolbar) toolbar.insertBefore(button, toolbar.firstChild);
            }

            // v2.5: los puntos ya salían redondeados (antialiasing de v2.4), pero
            // seguían viéndose "pegados" formando líneas rectas. La causa NO estaba
            // en la forma del punto sino en el ÁNGULO de la trama: Semitonos Fáciles
            // usa siempre 45° (FIXED_ANGLE_RAD), que es el ángulo estándar de
            // cualquier trama de semitono de una sola tinta precisamente porque a 0°/
            // 90° las filas de puntos quedan alineadas con la cuadrícula de pixeles y,
            // al crecer, se pegan en líneas rectas horizontales/verticales muy
            // visibles (efecto moiré clásico de imprenta). A 45° los puntos quedan
            // intercalados (cada fila desplazada de la anterior) y al crecer se unen
            // de forma mucho más orgánica, sin formar líneas. Aquí se fija 45° como
            // punto de partida de PRO, igual que Fáciles, sin quitarle al usuario la
            // posibilidad de moverlo con el control deslizante si así lo prefiere.
            if (screenAngleInput && !screenAngleInput.dataset.novageAngleInitialized) {
                screenAngleInput.dataset.novageAngleInitialized = 'true';
                screenAngleInput.value = 45;
                if (screenAngleValue) screenAngleValue.textContent = '45°';
            }

            // Capturar defaults después de fijar el ángulo recomendado de 45°.
            captureDefaultEditState();
            installResetEditsButton();

            // Inicializar UI
            updateUIForMode();
            refreshEditControlLabels();
})();